import { CCSCondition, FormSection } from "./types";

const _PATIENT_SECTION: FormSection = {
  id: "patient",
  title: "Patient Details",
  fields: [
    { id: "patient_name", label: "Patient Name", value: "", type: "text", required: true, placeholder: "Full name" },
    { id: "patient_dob", label: "Date of Birth", value: "", type: "date", required: true, placeholder: "DD/MM/YYYY" },
    { id: "patient_age", label: "Age (auto-calculated)", value: "", type: "number", placeholder: "Auto-calculated from DOB", computed: true, displaySuffix: "years" },
    { id: "patient_address", label: "Address", value: "", type: "textarea", placeholder: "Home address" },
    { id: "patient_eircode", label: "EirCode", value: "", type: "eircode", placeholder: "e.g. A65 F4E2" },
    { id: "patient_ppsn", label: "PPSN", value: "", type: "ppsn", placeholder: "e.g. 1234567WA" },
    { id: "patient_phone", label: "Phone", value: "", type: "text", placeholder: "Contact number" },
    { id: "gp_name", label: "GP Name / Practice", value: "", type: "text", placeholder: "GP or practice name" },
    {
      id: "patient_consented",
      label: "Patient Consent Obtained",
      value: "",
      type: "radio",
      options: ["Yes", "No"],
      required: true,
    },
    {
      id: "eligible_for_service",
      label: "Patient Eligible for CCS Service",
      value: "",
      type: "radio",
      options: ["Yes", "No"],
      required: true,
      hint: "Auto-set to No if patient is under 6 months of age (calculated from Date of Birth)",
    },
  ],
};

const _MEDICAL_HISTORY_SECTION: FormSection = {
  id: "medical_history",
  title: "Medical History",
  fields: [
    { id: "current_medications", label: "Current Medications", value: "", type: "textarea", placeholder: "List all regular medications including OTC" },
    { id: "known_allergies", label: "Known Allergies / Intolerances", value: "", type: "textarea", placeholder: "Drug allergies, food allergies, etc." },
    { id: "relevant_conditions", label: "Relevant Medical Conditions", value: "", type: "textarea", placeholder: "e.g. diabetes, hypertension, asthma" },
    { id: "immunosuppressed", label: "Immunocompromised / On Immunosuppressants", value: "", type: "radio", options: ["Yes", "No"] },
    { id: "pregnant_bf", label: "Pregnant or Breastfeeding", value: "", type: "radio", options: ["Yes", "No", "N/A"] },
    { id: "previous_episode", label: "Previous Episode of Same Condition", value: "", type: "radio", options: ["Yes", "No"] },
    { id: "recent_antibiotics", label: "Recent Antibiotics (within 3 months)", value: "", type: "radio", options: ["Yes", "No"] },
    { id: "contact_lens_wearer", label: "Contact Lens Wearer", value: "", type: "radio", options: ["Yes", "No"], required: true },
  ],
};

const OUTCOME_SECTION: FormSection = {
  id: "outcome",
  title: "Consultation Outcome",
  fields: [
    {
      id: "outcome_decision",
      label: "Outcome Decision",
      value: "",
      type: "radio",
      options: ["Treatment supplied", "Referred to GP", "Referred to A&E", "Advised — no treatment needed", "Patient declined treatment"],
      required: true,
    },
    { id: "outcome_notes", label: "Outcome Notes", value: "", type: "textarea", placeholder: "Additional notes on outcome or clinical reasoning" },
    {
      id: "safety_netting",
      label: "Safety Netting Advice Given",
      value: "",
      type: "radio",
      options: ["Yes", "No"],
      required: true,
    },
    { id: "safety_netting_detail", label: "Safety Netting Detail", value: "", type: "textarea", placeholder: "e.g. Return if no improvement in 48h, worsening symptoms, or high fever develops" },
    { id: "follow_up_required", label: "Follow-Up Required", value: "", type: "radio", options: ["Yes – 2 days", "Yes – 7 days", "No"] },
    { id: "gp_notification", label: "GP Notification Sent", value: "", type: "radio", options: ["Yes", "No", "N/A"] },
    { id: "dispensing_pharmacist", label: "Dispensing Pharmacist", value: "", type: "text", placeholder: "Name of pharmacist dispensing" },
  ],
};

const SIGNATURE_SECTION: FormSection = {
  id: "declaration",
  title: "Patient Declaration & Signature",
  fields: [
    {
      id: "patient_is_under_16",
      label: "Patient Under 16?",
      value: "",
      type: "radio",
      options: ["Yes", "No"],
      required: true,
      hint: "Auto-calculated from Date of Birth when available",
    },
    {
      id: "pharmacist_declaration",
      label: "Pharmacist Declaration",
      value: "I confirm that I have assessed this patient and provided appropriate advice and/or treatment in accordance with the CCS service specification.",
      type: "textarea",
    },
    {
      id: "patient_signature",
      label: "Patient Signature",
      value: "",
      type: "signature",
      required: true,
    },
    {
      id: "guardian_signature",
      label: "Parent/Guardian Signature (if under 16)",
      value: "",
      type: "signature",
    },
  ],
};

const CONJUNCTIVITIS_PATIENT_SECTION: FormSection = {
  id: "patient",
  title: "Patient Details",
  fields: [
    { id: "patient_name", label: "Patient Name", value: "", type: "text", required: true, placeholder: "Full name" },
    { id: "patient_address", label: "Address", value: "", type: "textarea", placeholder: "Home address" },
    { id: "patient_eircode", label: "Eircode", value: "", type: "eircode", placeholder: "e.g. A65 F4E2" },
    { id: "patient_ppsn", label: "PPSN", value: "", type: "ppsn", placeholder: "e.g. 1234567WA" },
    { id: "patient_phone", label: "Contact Number", value: "", type: "text", placeholder: "Contact number" },
    {
      id: "scheme",
      label: "Scheme Number",
      value: "",
      type: "radio",
      options: ["GMS", "DPS", "Private", "GP Visit Card"],
      required: true,
    },
    { id: "patient_dob", label: "Date of Birth", value: "", type: "date", required: true, placeholder: "DD/MM/YYYY" },
    {
      id: "preterm_birth",
      label: "Confirm pre-term birth — use corrected age",
      value: "",
      type: "radio",
      options: ["Yes", "No"],
      hint: "Only shown for infants under 18 months. Tick Yes if the infant was born pre-term and corrected age should be used for eligibility.",
      showIf: { fieldId: "patient_is_under_18months", value: "Yes" },
    },
    {
      id: "preterm_weeks",
      label: "Weeks pre-term",
      value: "",
      type: "number",
      showIf: { fieldId: "preterm_birth", value: "Yes" },
      placeholder: "e.g. 6",
      hint: "Number of weeks early the infant was born. This will be subtracted when calculating corrected age.",
    },
    { id: "patient_age", label: "Age (auto-calculated)", value: "", type: "number", placeholder: "Auto-calculated from DOB", computed: true, displaySuffix: "years" },
    {
      id: "patient_sex",
      label: "Sex",
      value: "",
      type: "radio",
      options: ["Male", "Female", "Other"],
    },
    {
      id: "guardian_name",
      label: "Name of Parent / Guardian",
      value: "",
      type: "text",
      placeholder: "Required if patient is under 16 years",
      hint: "Complete this field if the patient is under 16 years of age.",
      showIf: { fieldId: "patient_is_under_16", value: "Yes" },
    },
    { id: "patient_consented", label: "Patient Consent Obtained", value: "", type: "radio", options: ["Yes", "No"], required: true },
    { id: "gp_name", label: "GP Name", value: "", type: "text", placeholder: "GP name (optional)" },
    { id: "gp_address", label: "GP Address", value: "", type: "textarea", placeholder: "GP address (optional)" },
    { id: "gp_phone", label: "GP Contact Number", value: "", type: "text", placeholder: "GP contact number (optional)" },
  ],
};

const CONJUNCTIVITIS_MEDICAL_HISTORY_SECTION: FormSection = {
  id: "medical_history",
  title: "Medical History",
  fields: [
    { id: "existing_conditions", label: "Existing Health Conditions", value: "", type: "textarea", placeholder: "List any relevant medical conditions" },
    { id: "existing_medication", label: "Existing Medication", value: "", type: "textarea", placeholder: "List all current medications including OTC" },
    { id: "allergy_status", label: "Allergy Status", value: "", type: "textarea", placeholder: "List any known allergies or intolerances" },
    { id: "pregnant", label: "Pregnant", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Chloramphenicol is not recommended in pregnancy — use clinical judgement and check the SPC before prescribing.", type: "warning" } } },
    { id: "breastfeeding", label: "Breastfeeding", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Chloramphenicol is not recommended while breastfeeding — use clinical judgement and check the SPC before prescribing.", type: "warning" } } },
    { id: "renal_impairment", label: "Any known renal (kidney) impairment?", value: "", type: "radio", options: ["Yes", "No"] },
    { id: "hepatic_impairment", label: "Any known hepatic (liver) impairment?", value: "", type: "radio", options: ["Yes", "No"] },
    {
      id: "antimicrobial_resistance",
      label: "Is patient aware if they have resistance to previous antimicrobial treatment?",
      value: "",
      type: "radio",
      options: ["Yes", "No"],
    },
    {
      id: "antimicrobial_resistance_list",
      label: "Resistant antimicrobials — please list",
      value: "",
      type: "textarea",
      showIf: { fieldId: "antimicrobial_resistance", value: "Yes" },
      placeholder: "List antimicrobials patient is aware of resistance to",
    },
  ],
};

const CONJUNCTIVITIS_SECTIONS: FormSection[] = [
  CONJUNCTIVITIS_PATIENT_SECTION,
  CONJUNCTIVITIS_MEDICAL_HISTORY_SECTION,
  {
    id: "symptoms",
    title: "Presenting Complaint",
    fields: [
      {
        id: "chief_complaint",
        label: "Chief Complaint",
        value: "",
        type: "textarea",
        placeholder: "Document the symptoms of the presenting complaint as described by the patient.",
        hint: "Document the symptoms of the presenting complaint as described by the patient.",
      },
      {
        id: "medication_tried",
        label: "Any medication already tried for the acute infective conjunctivitis symptoms?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      {
        id: "medication_tried_list",
        label: "Please list medications tried",
        value: "",
        type: "textarea",
        showIf: { fieldId: "medication_tried", value: "Yes" },
        placeholder: "List medications already tried",
      },
    ],
  },
  {
    id: "exclusions_emergency",
    title: "Exclusions — Emergency Referral",
    fields: [
      { id: "exc_em_scleritis", label: "Suspected scleritis", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_em_shingles", label: "Rash and/or vesicles around eye, eyelid or nose", value: "", type: "radio", options: ["Yes", "No"], hint: "Consider shingles (Herpes zoster) assessment." },
      { id: "exc_em_herpes_simplex", label: "Herpes simplex viral infection of the ocular area is suspected", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_em_chlamydia_neonate", label: "Suspected Chlamydia trachomatis infection in the first month of life", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_em_adenoviral", label: "Suspected adenoviral conjunctivitis due to severity of hyperaemia", value: "", type: "radio", options: ["Yes", "No"], hint: "Often associated with pain and light sensitivity." },
      { id: "exc_em_vision_loss", label: "Changes / loss in vision or visual acuity", value: "", type: "radio", options: ["Yes", "No"], hint: "Includes blurred vision or seeing halos." },
      { id: "exc_em_photophobia", label: "Severe photophobia and/or severe foreign body sensation", value: "", type: "radio", options: ["Yes", "No"], hint: "Patient cannot hold eye open." },
      { id: "exc_em_contact_lenses", label: "Individual wearing contact lenses", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Advise patient to remove contact lenses while awaiting referral." },
      { id: "exc_em_irregular_pupil", label: "Irregular pupil(s)", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_em_ciliary_flush", label: "Ciliary flush, especially if unilateral", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_em_severe_pain", label: "Moderate to severe eye pain", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_em_corneal_opacity", label: "Visible corneal opacity or haze", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_em_trauma", label: "History of recent significant ocular trauma", value: "", type: "radio", options: ["Yes", "No"], hint: "Includes recent eye surgery, welding flashes, high-velocity power tools." },
    ],
  },
  {
    id: "exclusions_gp",
    title: "Exclusions — Immediate GP Referral",
    fields: [
      {
        id: "exc_gp_under6months",
        label: "Individual under 6 months of age",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "Auto-filled from Date of Birth. Corrected age used if pre-term birth is indicated in Patient Details.",
      },
      { id: "exc_gp_contraindications", label: "Contraindications as specified in the medication SPC", value: "", type: "radio", options: ["Yes", "No"] },
      {
        id: "exc_gp_ocular_disease",
        label: "History of ocular disease (e.g. keratitis, scleritis, iritis)",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "Topical glaucoma drops are not a reason for referral unless no improvement after 2 days of treatment.",
      },
      { id: "exc_gp_hyperpurulent", label: "Hyper-purulent discharge (yellow/green) with very rapid onset", value: "", type: "radio", options: ["Yes", "No"], yesNote: "May indicate Neisseria gonorrhoea eye infection. Refer." },
      { id: "exc_gp_chlamydia_older", label: "Suspected Chlamydia trachomatis infection in individuals aged 1 month and older", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_gp_lid_swollen_closed", label: "Both upper and lower lid intensely swollen / closed with no palpable lesion and difficult to open", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Risk of preseptal or orbital cellulitis. Refer." },
      { id: "exc_gp_meibomian_cellulitis", label: "Entire lid swollen with dark red presentation around a localised initial lump or swelling", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Risk of meibomian cyst with localised cellulitis. Refer." },
      { id: "exc_gp_minor_trauma", label: "Minor ocular trauma (e.g. fingernail, branch)", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_gp_severe_headache", label: "Severe headache with or without nausea", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_gp_focal_redness", label: "Focal redness (not diffuse redness)", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_gp_reinfection", label: "Recent reinfection with relapse within two weeks", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_gp_not_improving", label: "Treatment initiated and not improving after 2 days", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_gp_hypersensitivity", label: "Known hypersensitivity or adverse reaction to chloramphenicol, fusidic acid, or formulation components", value: "", type: "radio", options: ["Yes", "No"] },
      {
        id: "exc_gp_summary",
        label: "Are there any Red Flag or Referral Criteria present?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "Auto-filled based on the criteria above — you may override and add a reason below.",
        computed: true,
      },
      {
        id: "exc_gp_referral_reason",
        label: "Reason for referral",
        value: "",
        type: "textarea",
        showIf: { fieldId: "exc_gp_summary", value: "Yes" },
        placeholder: "Document reason for referral",
      },
    ],
  },
  {
    id: "review_of_symptoms",
    title: "Review of Symptoms",
    fields: [
      {
        id: "bact_symptoms",
        label: "Bacterial Conjunctivitis — Tick all typical features present",
        value: "",
        type: "checkbox",
        options: [
          "Irritated red eye with dilated / injected vessels on the conjunctival lining across the entire white of the eye",
          "Pink mild swelling in the eyelid",
          "Purulent or mucopurulent discharge (typically yellow-white), collected inside inner eyelid or actively discharging",
          "Eye often stuck closed in the morning",
          "Often unilateral",
        ],
      },
      {
        id: "viral_symptoms",
        label: "Viral Conjunctivitis — Tick all typical features present",
        value: "",
        type: "checkbox",
        options: [
          "Irritated red eye with dilated / injected vessels on the conjunctival lining across the entire white of the eye",
          "Pink mild swelling in the eyelid",
          "Watery or mucoserous discharge",
          "Sensation of grittiness, burning, or irritation",
          "Crusting on lid margin in the morning",
          "Associated viral symptoms (e.g. common cold, sore throat)",
          "Can be unilateral or bilateral",
        ],
      },
      {
        id: "symptoms_typical",
        label: "Are symptoms typical of acute infective conjunctivitis?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        required: true,
      },
      {
        id: "symptoms_not_typical_reason",
        label: "Reason (if not typical)",
        value: "",
        type: "textarea",
        showIf: { fieldId: "symptoms_typical", value: "No" },
        placeholder: "Document reason symptoms are not typical of acute infective conjunctivitis",
      },
      {
        id: "conjunctivitis_classification",
        label: "Classification",
        value: "",
        type: "radio",
        options: ["Bacterial conjunctivitis", "Viral conjunctivitis"],
        showIf: { fieldId: "symptoms_typical", value: "Yes" },
        hint: "Select classification — this determines the treatment pathway.",
        required: true,
      },
    ],
  },
  OUTCOME_SECTION,
  {
    id: "treatment",
    title: "Treatment & Advice",
    fields: [
      {
        id: "treatment_option",
        label: "Prescribing Option",
        value: "",
        type: "radio",
        options: [
          "Option A — Chloramphenicol 0.5% w/v eye drops",
          "Option B — Chloramphenicol 1% w/w ointment (unlicensed)",
          "Option C — Fusidic Acid 10mg/g viscous eye drops suspension",
        ],
        valueNotes: {
          "Option A — Chloramphenicol 0.5% w/v eye drops": { message: "Use with caution if patient is under 2 years. Not recommended in pregnancy or breastfeeding — see Option C.", type: "warning" },
          "Option B — Chloramphenicol 1% w/w ointment (unlicensed)": { message: "Unlicensed medication. May blur vision when applied during the day. Not recommended in pregnancy or breastfeeding — see Option C.", type: "warning" },
          "Option C — Fusidic Acid 10mg/g viscous eye drops suspension": { message: "Suitable for use in pregnancy and breastfeeding. Note: minimal Gram-negative activity.", type: "info" },
        },
      },
      { id: "treatment_dose", label: "Dose", value: "", type: "text", computed: true, placeholder: "Auto-filled from prescribing option" },
      { id: "treatment_frequency", label: "Frequency", value: "", type: "text", computed: true, placeholder: "Auto-filled from prescribing option" },
      { id: "treatment_duration", label: "Duration", value: "", type: "text", computed: true, placeholder: "Auto-filled from prescribing option" },
      { id: "treatment_quantity", label: "Quantity Supplied", value: "", type: "number", placeholder: "Quantity" },
      { id: "treatment_brand", label: "Brand (optional)", value: "", type: "text", placeholder: "Brand name if applicable" },
      {
        id: "counselling_checklist",
        label: "Counselling Provided — Tick all items discussed with patient",
        value: "",
        type: "checkbox",
        options: [
          "Wash hands often with warm soapy water",
          "Wash hands immediately after touching eyes or applying drops / ointment",
          "Avoid touching or rubbing the eyes",
          "Wash pillows and face cloths in hot water and detergent",
          "Do not wear contact lenses until antibiotic course is complete and all symptoms have resolved",
          "Do not share towels or pillows",
          "Children with conjunctivitis do not need to stay out of school or childcare if otherwise well — inform school / childcare provider",
          "Safety netting: seek medical advice if no improvement after 48 hours of treatment, or if symptoms persist beyond one week",
        ],
      },
      { id: "advice_notes", label: "Additional Notes", value: "", type: "textarea", placeholder: "Any additional clinical notes or advice given" },
    ],
  },
];

const RHINITIS_PATIENT_SECTION: FormSection = {
  id: "patient",
  title: "Patient Details",
  fields: [
    { id: "patient_name", label: "Patient Name", value: "", type: "text", required: true, placeholder: "Full name" },
    { id: "patient_dob", label: "Date of Birth", value: "", type: "date", required: true, placeholder: "DD/MM/YYYY" },
    { id: "patient_age", label: "Age (auto-calculated)", value: "", type: "number", placeholder: "Auto-calculated from DOB", computed: true, displaySuffix: "years" },
    {
      id: "ar_age_warning",
      label: "Age Eligibility",
      value: "",
      type: "text",
      computed: true,
      placeholder: "Checking age eligibility...",
      hint: "Minimum age for AR/AC CCS service is 2 years.",
      valueNotes: {
        "under_2_years": { message: "Age under 2 years — REFER to GP or other relevant medical practitioner. Pharmacist prescribing is not permitted (Protocol Section 2.4.2).", type: "danger" },
      },
    },
    { id: "patient_sex", label: "Sex", value: "", type: "radio", options: ["Male", "Female", "Other"] },
    { id: "patient_address", label: "Address", value: "", type: "textarea", placeholder: "Home address" },
    { id: "patient_eircode", label: "EirCode", value: "", type: "eircode", placeholder: "e.g. A65 F4E2" },
    { id: "patient_ppsn", label: "PPSN", value: "", type: "ppsn", placeholder: "e.g. 1234567WA" },
    { id: "patient_phone", label: "Phone", value: "", type: "text", placeholder: "Contact number" },
    { id: "scheme", label: "Scheme", value: "", type: "radio", options: ["GMS", "DPS", "GP Visit Card", "Private"], required: true },
    {
      id: "guardian_name",
      label: "Parent / Guardian Name",
      value: "",
      type: "text",
      placeholder: "Required if patient is under 16",
      hint: "Complete this field if the patient is under 16 years of age.",
    },
    { id: "gp_name", label: "GP Name / Practice", value: "", type: "text", placeholder: "Optional" },
    { id: "gp_address", label: "GP Address", value: "", type: "textarea", placeholder: "Optional" },
    { id: "gp_phone", label: "GP Contact Number", value: "", type: "text", placeholder: "Optional" },
    { id: "patient_consented", label: "Patient Consent Obtained", value: "", type: "radio", options: ["Yes", "No"], required: true },
  ],
};

const RHINITIS_SECTIONS: FormSection[] = [
  RHINITIS_PATIENT_SECTION,
  {
    id: "presenting_complaint",
    title: "Presenting Complaint",
    fields: [
      {
        id: "ar_presenting_symptoms",
        label: "Which symptoms is the patient presenting with?",
        value: "",
        type: "checkbox",
        required: true,
        options: ["Nasal symptoms (Allergic Rhinitis)", "Eye symptoms (Allergic Conjunctivitis)", "Both nasal and eye symptoms"],
        hint: "Tick all that apply. This drives the treatment selector later.",
      },
      { id: "ar_has_nasal", label: "", value: "", type: "radio", options: ["Yes", "No"], hidden: true, computed: true },
      { id: "ar_has_eye", label: "", value: "", type: "radio", options: ["Yes", "No"], hidden: true, computed: true },
      {
        id: "ar_chief_complaint",
        label: "Chief Complaint",
        value: "",
        type: "textarea",
        required: true,
        placeholder: "Document the symptoms as described by the patient, in their own words.",
        hint: "Document the symptoms as described by the patient, in their own words.",
      },
      {
        id: "ar_duration",
        label: "Duration of Symptoms",
        value: "",
        type: "radio",
        required: true,
        options: ["Less than 1 week", "1–2 weeks", "2–4 weeks", "More than 4 weeks"],
      },
      {
        id: "ar_frequency",
        label: "Frequency",
        value: "",
        type: "radio",
        required: true,
        options: [
          "Intermittent (less than 4 days/week OR less than 4 weeks/episode)",
          "Persistent (more than 4 days/week OR longer than 4 weeks)",
        ],
        hint: "Per ARIA classification. Doesn't change treatment choice but informs duration.",
      },
      {
        id: "ar_severity",
        label: "Severity (ARIA)",
        value: "",
        type: "radio",
        required: true,
        options: [
          "Mild — no troublesome symptoms, no impact on school / work / sleep / leisure",
          "Moderate — troublesome but little impact on day-to-day activities",
          "Severe — very troublesome, impaired school / work / leisure, or sleep disturbance",
        ],
        hint: "Severity drives treatment choice per ARIA guidelines. A Visual Analogue Scale can be used if helpful.",
      },
      {
        id: "ar_trigger",
        label: "Known or Suspected Allergen / Trigger",
        value: "",
        type: "text",
        placeholder: "e.g. pollen, dust mites, pet dander, mould, cosmetics",
        hint: "Helps target self-care advice.",
      },
      {
        id: "ar_medication_tried",
        label: "Medication Already Tried for This Episode",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
      },
      {
        id: "ar_medication_tried_list",
        label: "Medications Tried — List",
        value: "",
        type: "textarea",
        showIf: { fieldId: "ar_medication_tried", value: "Yes" },
        placeholder: "Medication(s), dose, and duration",
      },
      {
        id: "ar_symptoms_controlled",
        label: "Symptoms Controlled?",
        value: "",
        type: "radio",
        showIf: { fieldId: "ar_medication_tried", value: "Yes" },
        options: ["Yes", "Partially", "No"],
        hint: "If not controlled or only partially controlled, GP referral required — initial limited supply permitted.",
      },
      {
        id: "ar_asthma_diagnosed",
        label: "Asthma Screening — Has a doctor ever diagnosed the patient with asthma?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        hint: "Allergic rhinitis and asthma commonly co-exist. Always screen.",
        yesNote: "Note: if asthma is uncontrolled, GP referral is required. INCS is preferred over oral antihistamine when asthma co-exists.",
      },
      {
        id: "ar_asthma_symptoms",
        label: "Asthma Screening — Any wheezing, breathlessness, or asthma exacerbation in the last 12 months?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        showIf: { fieldId: "ar_asthma_diagnosed", value: "Yes" },
      },
      {
        id: "ar_asthma_medication",
        label: "Asthma Screening — Currently taking any asthma medication?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        showIf: { fieldId: "ar_asthma_diagnosed", value: "Yes" },
        valueNotes: {
          "No": { message: "Note: if asthma is uncontrolled, GP referral is required.", type: "warning" },
        },
      },
    ],
  },
  {
    id: "medical_history",
    title: "Medical History",
    fields: [
      {
        id: "existing_conditions",
        label: "Existing Health Conditions",
        value: "",
        type: "textarea",
        placeholder: "List relevant medical conditions (e.g. asthma, eczema, hay fever history)",
      },
      {
        id: "existing_medications",
        label: "Existing Medications",
        value: "",
        type: "textarea",
        required: true,
        placeholder: "List all current medications including OTC, supplements, and other corticosteroid treatments",
        hint: "List all current medications including OTC, supplements, and other corticosteroid treatments (inhalers, topical creams, eye drops). Total systemic corticosteroid burden matters.",
      },
      {
        id: "known_allergies",
        label: "Known Allergies",
        value: "",
        type: "textarea",
        required: true,
        placeholder: "Include hypersensitivity to any antihistamine or INCS",
        hint: "Include hypersensitivity to any antihistamine or intranasal corticosteroid. Note any history of QT prolongation or Torsade de Pointes (caution with some antihistamines).",
      },
      {
        id: "pregnant",
        label: "Pregnant or Suspected Pregnancy",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No", "Not applicable"],
        hint: "Pregnancy: refer to GP, treating obstetrician, or gynaecologist. Pharmacist prescribing not permitted.",
      },
      {
        id: "breastfeeding",
        label: "Currently Breastfeeding",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No", "Not applicable"],
        valueNotes: {
          "Yes": { message: "Pharmacist may proceed but should use clinical judgement and check medication SPC.", type: "warning" },
        },
      },
      {
        id: "renal_impairment",
        label: "Known Renal (Kidney) Impairment",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No", "Unknown"],
      },
      {
        id: "hepatic_impairment",
        label: "Known Hepatic (Liver) Impairment",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No", "Unknown"],
      },
      {
        id: "ar_drug_resistance",
        label: "Known Drug Resistance to Previous Treatment",
        value: "",
        type: "radio",
        options: ["Yes", "No", "Unknown"],
      },
      {
        id: "ar_drug_resistance_detail",
        label: "Drug Resistance — Please List",
        value: "",
        type: "text",
        showIf: { fieldId: "ar_drug_resistance", value: "Yes" },
        placeholder: "List medications",
      },
    ],
  },
  {
    id: "exclusions_emergency",
    title: "Exclusion Criteria",
    description: "Emergency — call ambulance immediately. Pharmacists with training may use emergency medicines if clinically appropriate.",
    fields: [
      {
        id: "exc_ar_em_anaphylaxis",
        label: "Symptoms of anaphylaxis",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "Swollen tongue, breathing difficulties, tight chest, trouble swallowing or speaking, dizziness, faintness, collapse.",
        valueNotes: {
          "Yes": { message: "Emergency referral required — call emergency services. Pharmacists with requisite training should consider use of emergency medicines if clinically appropriate (SI 449/2015).", type: "danger" },
        },
      },
      {
        id: "exc_ar_em_severe_asthma",
        label: "Symptoms of severe asthma attack",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: {
          "Yes": { message: "Emergency referral required — call emergency services. Pharmacists with requisite training should consider use of emergency medicines if clinically appropriate (SI 449/2015).", type: "danger" },
        },
      },
    ],
  },
  {
    id: "exclusions_gp",
    title: "Exclusion Criteria",
    description: "Refer to GP. Pharmacist prescribing is not permitted.",
    fields: [
      { id: "exc_ar_gp_under2", label: "Under 2 years of age", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Age under 2 years — REFER to GP or other relevant medical practitioner. Pharmacist prescribing is not permitted (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_contraindication", label: "Contraindication per SPC", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Contraindication identified — REFER to GP (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_pregnancy", label: "Pregnancy or suspected pregnancy", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Pregnancy or suspected pregnancy — REFER to GP, treating obstetrician, or gynaecologist. Pharmacist prescribing is not permitted (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_hypersensitivity", label: "Known hypersensitivity to a protocol treatment", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Known hypersensitivity to a protocol treatment option — REFER to GP (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_sinusitis", label: "Suspected sinusitis infection or continuous mucopurulent discharge", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Mucopurulent discharge suggests infection — REFER to GP (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_otitis_media", label: "Suspected ear infection, e.g. otitis media", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Suspected otitis media or sinusitis — REFER to GP (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_drug_induced", label: "Suspected drug-induced rhinitis", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Suspected drug-induced rhinitis — REFER to GP (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_persistent_headache", label: "Persistent headache, eye, or facial pain", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Persistent headache, eye, or facial pain — REFER to GP (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_unilateral_nasal", label: "Nasal symptoms in one nostril only", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Unilateral nasal symptoms — REFER to GP (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_steroids_child", label: "Child under 18 on systemic, medium/high-dose inhaled, or continuous potent topical corticosteroids", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Child under 18 on significant concurrent corticosteroid treatment — REFER to GP. Risk of effects on growth (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_blurred_vision", label: "Blurred vision", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Blurred vision — REFER to GP for further assessment (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_incs_8weeks", label: "INCS already used continuously for 8 weeks in a patient under 18", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "INCS used continuously for 8 weeks in a child under 18 — REFER to GP. Maximum pharmacist-prescribed duration is 8 weeks (Protocol Section 2.4.2).", type: "danger" } } },
      { id: "exc_ar_gp_epistaxis", label: "Coughing blood, problems swallowing, or recurrent epistaxis", value: "", type: "radio", options: ["Yes", "No"], hint: "Symptoms not typical of allergic rhinitis.", valueNotes: { "Yes": { message: "Symptoms not typical of allergic rhinitis — REFER to GP (Protocol Section 2.4.2).", type: "danger" } } },
    ],
  },
  {
    id: "exclusions_gp_limited_supply",
    title: "Exclusion Criteria",
    description: "GP referral required. Pharmacist MAY consider initial limited supply to bridge access to the referral pathway.",
    fields: [
      { id: "exc_ar_ls_uncontrolled", label: "Symptoms not sufficiently controlled under this protocol", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Symptoms not sufficiently controlled under previous treatment — GP referral required, initial limited supply permitted (Protocol Section 2.4.3).", type: "warning" } } },
      { id: "exc_ar_ls_asthma", label: "Dyspnoea, wheezing, or uncontrolled asthma", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Possible asthma — refer to a medical practitioner. INCS preferred over oral antihistamine. Pharmacist may consider providing AR/AC treatment in the interim if clinically appropriate (Protocol Section 2.4.3).", type: "warning" } } },
      { id: "exc_ar_ls_nasal_surgery", label: "Recent nasal surgery or trauma", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Recent nasal surgery or trauma — GP referral required, initial limited supply permitted (Protocol Section 2.4.3).", type: "warning" } } },
      {
        id: "exc_ar_ls_vernal",
        label: "Suspected vernal conjunctivitis",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "Chronic allergic eye inflammation. Suspect if thick swollen lids (papillae), secondary corneal changes, blurred vision, intense eye rubbing, or lack of response to antihistamine/mast cell stabiliser after 14 days.",
        valueNotes: { "Yes": { message: "Suspected vernal conjunctivitis — GP referral required, initial limited supply permitted (Protocol Section 2.4.3).", type: "warning" } },
      },
      { id: "exc_ar_ls_unilateral_eye", label: "Unilateral eye symptoms", value: "", type: "radio", options: ["Yes", "No"], hint: "Consider Acute Infective Conjunctivitis protocol.", valueNotes: { "Yes": { message: "Unilateral eye symptoms — consider Acute Infective Conjunctivitis protocol, or refer to a medical practitioner.", type: "warning" } } },
      { id: "exc_ar_ls_photophobia", label: "Significant photophobia (moderate to severe)", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Significant photophobia — consider referral to a medical practitioner; not typical of allergic conjunctivitis.", type: "warning" } } },
      { id: "exc_ar_ls_eye_pain", label: "Eye pain", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Eye pain is not typical of allergic conjunctivitis — consider alternative diagnosis or referral.", type: "warning" } } },
      { id: "exc_ar_ls_eye_vas", label: "Severe eye symptoms (VAS ≥ 5)", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Severe eye symptoms (VAS ≥ 5) — refer to a medical practitioner.", type: "warning" } } },
    ],
  },
  {
    id: "review_of_symptoms",
    title: "Review of Symptoms",
    description: "Confirm the presentation is typical of allergic rhinitis and/or allergic conjunctivitis before proceeding.",
    fields: [
      {
        id: "ar_rhinitis_frame",
        label: "Allergic Rhinitis — Typical Symptoms",
        value: "",
        type: "checkbox",
        options: [
          "Rhinorrhoea (watery discharge)",
          "Sneezing",
          "Itchy nose, eyes or palate",
          "Nasal congestion",
          "Irritability or fatigue (particularly in young children)",
          "In children, there may be the presence of a transverse nasal crease (allergic salute) caused by rubbing of the nose.",
          "Reduction of smell (severe cases)",
          "May or may not be accompanied by symptoms of allergic conjunctivitis",
        ],
        hint: "Tick the features present.",
      },
      {
        id: "ar_conjunctivitis_frame",
        label: "Allergic Conjunctivitis — Typical Symptoms",
        value: "",
        type: "checkbox",
        options: [
          "Irritated red eye with dilated/injected vessels (red inflamed blood vessels) on the conjunctival lining of the white of the eye (sclera) across the entire white of the eye",
          "Eye itching",
          "Pink mild swelling in the eyelid (puffy eyes)",
          "Profuse watery or mucoserous, stringy discharge",
          "Sensation of grittiness, burning, or irritation",
          "Crusting on lid margin in the morning",
          "Often accompanied with symptoms associated with allergic rhinitis",
          "Signs can be unilateral or bilateral but are more often bilateral in allergic conjunctivitis",
        ],
        hint: "Tick the features present.",
      },
      {
        id: "ar_typical_confirmed",
        label: "Are the symptoms typical of allergic rhinitis and/or allergic conjunctivitis?",
        value: "",
        type: "radio",
        required: true,
        options: [
          "Yes — Allergic Rhinitis",
          "Yes — Allergic Conjunctivitis",
          "Yes — Both Allergic Rhinitis and Allergic Conjunctivitis",
          "No — refer and document",
        ],
      },
      {
        id: "ar_atypical_reason",
        label: "Reason for Referral",
        value: "",
        type: "textarea",
        showIf: { fieldId: "ar_typical_confirmed", value: "No — refer and document" },
        placeholder: "Document reason why presentation is not typical",
        hint: "Presentation is not typical of allergic rhinitis or allergic conjunctivitis — REFER and document reason.",
      },
    ],
  },
  {
    id: "outcome",
    title: "Consultation Outcome",
    fields: [
      {
        id: "outcome_decision",
        label: "Consultation Outcome",
        value: "",
        type: "radio",
        required: true,
        options: [
          "Prescription for POM supplied (full course)",
          "Prescription for POM supplied (initial limited supply)",
          "OTC product supplied",
          "Self-care advice only",
          "Referral",
        ],
      },
      {
        id: "outcome_referred_to",
        label: "Referred to",
        value: "",
        type: "select",
        showIf: { fieldId: "outcome_decision", value: "Referral" },
        options: [
          "Accident & Emergency Department",
          "General Practitioner (GP)",
          "GP Out-of-Hours",
          "Ophthalmology",
          "Specialist in allergies",
          "Other",
        ],
      },
      {
        id: "outcome_referral_reason",
        label: "Reason for Referral",
        value: "",
        type: "textarea",
        showIf: { fieldId: "outcome_decision", value: "Referral" },
        placeholder: "Document clinical reasoning for referral",
      },
      {
        id: "gp_notification",
        label: "GP Notification Sent",
        value: "",
        type: "radio",
        options: ["Yes", "No", "Not applicable"],
      },
      {
        id: "ar_follow_up_required",
        label: "Follow-Up Required",
        value: "",
        type: "radio",
        options: [
          "Yes — 7–10 day review",
          "Yes — 4–6 week review (child on INCS)",
          "Yes — end of 8-week INCS course (child under 18)",
          "No",
        ],
      },
      {
        id: "ar_follow_up_detail",
        label: "Follow-Up Details",
        value: "",
        type: "text",
        placeholder: "Date, method, or instructions",
      },
    ],
  },
  {
    id: "treatment",
    title: "Treatment & Advice",
    fields: [
      {
        id: "ar_proceed",
        label: "Proceed with Pharmacist Prescribing?",
        value: "",
        type: "radio",
        required: true,
        options: [
          "Yes — full treatment per protocol",
          "Yes — initial limited supply (bridge to GP referral)",
          "No — refer",
          "No — patient declined treatment",
          "No — self-care and allergen avoidance advice only",
        ],
      },
      {
        id: "ar_refer_reason",
        label: "Reason for Referral / Declining",
        value: "",
        type: "textarea",
        showIf: { fieldId: "ar_proceed", value: "No — refer" },
        placeholder: "Document reason",
      },
      {
        id: "ar_limited_supply_duration",
        label: "Duration of Initial Limited Supply Provided",
        value: "",
        type: "text",
        showIf: { fieldId: "ar_proceed", value: "Yes — initial limited supply (bridge to GP referral)" },
        placeholder: "e.g. 7 days",
        hint: "Limit to dose or time necessary to access the referral pathway.",
      },
      {
        id: "ar_nasal_treatment",
        label: "6A — Nasal Treatment Selected",
        value: "",
        type: "dropdown",
        required: true,
        options: [
          "— INCS 2nd Generation (preferred) —",
          "Fluticasone furoate 27.5 mcg/spray (2nd gen INCS)",
          "Fluticasone propionate 50 mcg/spray (2nd gen INCS)",
          "Mometasone furoate 50 mcg/spray (2nd gen INCS)",
          "— INCS 1st Generation (adults 18+ only) —",
          "Beclometasone dipropionate 50 mcg/spray (1st gen INCS — adults only)",
          "Triamcinolone acetonide 55 mcg/dose (1st gen INCS — adults only)",
          "— Combination INCS + Intranasal Antihistamine —",
          "Azelastine HCl / Fluticasone propionate 137/50 mcg/actuation",
          "Mometasone furoate / Olopatadine 25/600 mcg/actuation",
          "— Intranasal Antihistamine Spray —",
          "Azelastine hydrochloride 140 mcg/spray",
          "— Oral Non-Sedating Antihistamine — 2nd Generation —",
          "Cetirizine dihydrochloride 10 mg tablets OR 1 mg/ml oral solution",
          "Loratadine 10 mg tablets",
          "Bilastine 10 mg orodispersible OR 20 mg tablets OR 2.5 mg/ml oral solution",
          "— Oral Non-Sedating Antihistamine — 3rd Generation —",
          "Desloratadine 5 mg tablets OR 0.5 mg/ml oral solution",
          "Fexofenadine hydrochloride 120 mg tablets",
          "Levocetirizine dihydrochloride 5 mg tablets OR 0.5 mg/ml oral solution",
          "Not applicable",
        ],
        hint: "MILD: Oral AH or INCS (INCS preferred if congestion is main symptom or coexisting asthma). MODERATE: INCS, intranasal AH, or step-up with oral AH add-on. SEVERE: INCS + intranasal AH combination (combo product preferred), step-up with oral AH add-on. 1st-gen INCS not for under 18s. See SPC for dosing and age licensing.",
      },
      {
        id: "ar_under18_incs_note",
        label: "Under-18 INCS Monitoring Note",
        value: "Review after 4–6 weeks to assess response. Maximum pharmacist-prescribed duration is 8 weeks. Refer to GP if longer treatment is needed. Monitor growth if on concurrent corticosteroid treatments.",
        type: "text",
        computed: true,
        showIf: { fieldId: "ar_has_nasal", value: "Yes" },
      },
      {
        id: "ar_eye_treatment",
        label: "6B — Eye Treatment Selected",
        value: "",
        type: "dropdown",
        required: true,
        options: [
          "— Intraocular Cromone (Mast Cell Stabiliser) —",
          "Sodium cromoglicate 2% w/v eye drops, solution",
          "— Intraocular Antihistamine —",
          "Ketotifen 0.25 mg/ml eye drops, solution",
          "Olopatadine hydrochloride 1 mg/ml eye drops, solution",
          "Cromone + Antihistamine eye drops (moderate)",
          "Refer to medical practitioner (severe eye symptoms — do not prescribe)",
          "Not applicable",
        ],
        hint: "MILD or MODERATE: Cromone eye drops (mast cell stabiliser) OR antihistamine eye drops, OR both (moderate). SEVERE: Refer to a medical practitioner — do not prescribe. Cromones reach full efficacy after 5–14 days. Antihistamine drops work within minutes. Start cromones 2–4 weeks before pollen season for seasonal AC.",
      },
      {
        id: "ar_advice_given",
        label: "Self-Care and Safety Netting Advice",
        value: "",
        type: "bullets",
        options: [
          "Allergen avoidance advice — tailored to identified trigger (e.g. pollen, dust mites, pet dander)",
          "Nasal spray technique demonstrated (if nasal spray prescribed)",
          "Eye drop administration advice given (if eye drops prescribed)",
          "Advise patient to return if no improvement after 7–10 days",
          "Advise patient to see GP if symptoms worsen or fail to respond to treatment",
          "Patient Information Leaflet (PIL) provided",
          "HSE A-Z and HSE app signposted",
        ],
      },
    ],
  },
];

const IMPETIGO_PATIENT_SECTION: FormSection = {
  id: "patient",
  title: "Patient Details",
  fields: [
    { id: "patient_name", label: "Patient Name", value: "", type: "text", required: true, placeholder: "Full name" },
    { id: "patient_address", label: "Address", value: "", type: "textarea", placeholder: "Home address" },
    { id: "patient_eircode", label: "Eircode", value: "", type: "eircode", placeholder: "e.g. A65 F4E2" },
    { id: "patient_ppsn", label: "PPSN", value: "", type: "ppsn", placeholder: "e.g. 1234567WA" },
    { id: "patient_phone", label: "Contact No.", value: "", type: "text", placeholder: "Contact number" },
    { id: "scheme", label: "Scheme Number", value: "", type: "radio", options: ["GMS", "DPS", "Private", "GP Visit Card"], required: true },
    { id: "patient_dob", label: "Date of Birth", value: "", type: "date", required: true, placeholder: "DD/MM/YYYY" },
    { id: "patient_age", label: "Age (auto-calculated)", value: "", type: "number", placeholder: "Auto-calculated from DOB", computed: true, displaySuffix: "years" },
    {
      id: "imp_age_warning",
      label: "Age Eligibility",
      value: "",
      type: "text",
      computed: true,
      placeholder: "Checking age eligibility...",
      hint: "Minimum age for Impetigo CCS service is 2 months (corrected age for pre-term infants).",
      valueNotes: {
        "under_2_months": { message: "Patient is under 2 months of age. Referral to GP required — see Section 4.3.", type: "danger" },
      },
    },
    { id: "patient_sex", label: "Sex", value: "", type: "radio", options: ["Male", "Female"] },
    {
      id: "guardian_name",
      label: "Name of Parent / Guardian (if patient under 16 years)",
      value: "",
      type: "text",
      placeholder: "Required if patient is under 16 years",
      showIf: { fieldId: "patient_is_under_16", value: "Yes" },
    },
    { id: "patient_consented", label: "Patient Consent Obtained", value: "", type: "radio", options: ["Yes", "No"], required: true },
    { id: "gp_name", label: "GP Name", value: "", type: "text", placeholder: "GP name", hint: "Not mandatory" },
    { id: "gp_address", label: "GP Address", value: "", type: "textarea", placeholder: "GP address", hint: "Not mandatory" },
    { id: "gp_phone", label: "GP Contact Number", value: "", type: "text", placeholder: "GP contact number", hint: "Not mandatory" },
  ],
};

const IMPETIGO_SECTIONS: FormSection[] = [
  IMPETIGO_PATIENT_SECTION,

  {
    id: "symptoms",
    title: "Presenting Complaint",
    fields: [
      {
        id: "chief_complaint",
        label: "Document the symptoms of the presenting complaint as described by the patient",
        value: "",
        type: "textarea",
        required: true,
        placeholder: "Patient's own description of their symptoms",
      },
    ],
  },

  {
    id: "medical_history",
    title: "Medical History",
    fields: [
      {
        id: "relevant_conditions",
        label: "Existing Health Conditions",
        value: "",
        type: "textarea",
        placeholder: "e.g. diabetes, atopic dermatitis",
      },
      {
        id: "current_medications",
        label: "Existing Medication",
        value: "",
        type: "textarea",
        placeholder: "List all regular medications including OTC",
      },
      {
        id: "known_allergies",
        label: "Allergy Status",
        value: "",
        type: "textarea",
        required: true,
        placeholder: "Drug allergies, food allergies, etc. Include any known hypersensitivity to fusidic acid or sodium fusidate.",
      },
      {
        id: "pregnant_bf",
        label: "Pregnant / Breastfeeding",
        value: "",
        type: "checkbox",
        options: [
          "Pregnant",
          "Breastfeeding (with impetigo lesion(s) on the breast)",
        ],
        hint: "Breastfeeding with lesion(s) on the breast requires GP referral — see Section 4.3.",
      },
      {
        id: "renal_impairment",
        label: "Known renal (kidney) impairment",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      {
        id: "hepatic_impairment",
        label: "Known hepatic (liver) impairment",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      {
        id: "medication_tried",
        label: "Any medication already tried for the management of non-bullous impetigo symptoms?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      {
        id: "medication_tried_list",
        label: "If yes, please list:",
        value: "",
        type: "textarea",
        placeholder: "List medications tried",
        showIf: { fieldId: "medication_tried", value: "Yes" },
      },
      {
        id: "antimicrobial_resistance",
        label: "Is the patient aware if they have resistance to a previous antimicrobial treatment?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      {
        id: "antimicrobial_resistance_detail",
        label: "If yes, please list:",
        value: "",
        type: "textarea",
        placeholder: "Detail antimicrobial resistance",
        showIf: { fieldId: "antimicrobial_resistance", value: "Yes" },
      },
    ],
  },

  {
    id: "exclusions_emergency",
    title: "Red Flag & Referral Criteria — Emergency Referral",
    description: "Refer urgently to Emergency Department via ambulance if any of the following apply:",
    fields: [
      {
        id: "exc_em_systemic",
        label: "Individual is systemically very unwell, or showing symptoms of severe/life-threatening infection, or systemic sepsis",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: {
          "Yes": {
            message: "Refer urgently to Emergency Department via ambulance.",
            type: "danger",
          },
        },
      },
    ],
  },

  {
    id: "exclusions_urgent_gp",
    title: "Red Flag & Referral Criteria — Urgent Medical Assessment",
    description: "Refer urgently (same day) to GP or GP Out-of-Hours service if any of the following apply:",
    fields: [
      {
        id: "exc_urg_serious_condition",
        label: "Signs and symptoms of a more serious condition or illness",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "e.g. affected area appears swollen and red, spreading redness, purple patches on the skin, pain, pus, area is warm to touch, or large blisters forming.",
        valueNotes: {
          "Yes": {
            message: "Refer urgently to GP or GP Out-of-Hours service.",
            type: "danger",
          },
        },
      },
      {
        id: "exc_urg_immunocompromise",
        label: "Individual has moderate to severe immunocompromise due to underlying medical conditions or treatments",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: {
          "Yes": {
            message: "Refer urgently to GP or GP Out-of-Hours service.",
            type: "danger",
          },
        },
      },
      {
        id: "exc_urg_complications",
        label: "Complications suspected",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "e.g. secondary infections of wounds, cellulitis or deeper soft tissue infection, scarlet fever.",
        valueNotes: {
          "Yes": {
            message: "Refer urgently to GP or GP Out-of-Hours service.",
            type: "danger",
          },
        },
      },
    ],
  },

  {
    id: "exclusions_gp",
    title: "Red Flag & Referral Criteria — GP Referral (Pharmacist Prescribing Not Permitted)",
    description: "Refer to GP if any of the following apply. Pharmacist prescribing under this protocol is not permitted:",
    fields: [
      {
        id: "exc_gp_under2months",
        label: "Individuals under 2 months of age (corrected age for pre-term infants)",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_contraindications",
        label: "Contraindications as specified in the medication Summary of Product Characteristics",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_breastfeeding_lesion",
        label: "Currently breastfeeding with impetigo lesion(s) present on the breast",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_bullous",
        label: "Bullous impetigo",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_widespread_nonbullous",
        label: "Widespread non-bullous impetigo — four or more lesions/clusters present",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_recurrent",
        label: "Recurrent impetigo — two or more prior episodes in the previous 12 months",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_underlying_skin",
        label: "Currently active underlying skin condition",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "e.g. Contact Dermatitis, Atopic Dermatitis, Scabies.",
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_open_wounds",
        label: "Any open wounds affecting the topical treatment application area or the immediate vicinity",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_failed_treatment",
        label: "Failed previous topical or oral treatment (including antimicrobials) for this episode of impetigo",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_fusidic_acid",
        label: "Extended, recurrent or use of fusidic acid within the previous 12 months — due to an increased risk of developing antibiotic resistance",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_open_flames",
        label: "The affected area(s) cannot be protected from open or naked flames (e.g. smokers) — risk of burns",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "Fusidic acid and sodium fusidate are flammable.",
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_hypersensitivity",
        label: "Known hypersensitivity or adverse reaction to medication treatment options, or any of the components within the formulation",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Pharmacist prescribing is not permitted.", type: "danger" } },
      },
      {
        id: "exc_gp_summary",
        label: "Are there any Red Flag or Referral Criteria present?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "Auto-filled based on the criteria above — you may override and add a reason below.",
        computed: true,
      },
      {
        id: "exc_gp_referral_reason",
        label: "Reason for referral",
        value: "",
        type: "textarea",
        placeholder: "Document the referral reason",
        showIf: { fieldId: "exc_gp_summary", value: "Yes" },
      },
    ],
  },

  {
    id: "exclusions_gp_limited_supply",
    title: "Red Flag & Referral Criteria — GP Referral (Initial Limited Supply)",
    description: "Refer to GP. Pharmacist may prescribe an initial limited supply to bridge access to the referral pathway:",
    fields: [
      {
        id: "exc_gp_ls_immunocompromised",
        label: "Individual is immunocompromised due to underlying medical conditions or treatments (excluding those with moderate to severe immunocompromise — see Section 4.2)",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: {
          "Yes": {
            message: "Refer to GP. Pharmacist may prescribe an initial limited supply sufficient only to bridge access to the referral pathway.",
            type: "warning",
          },
        },
      },
    ],
  },

  {
    id: "review_of_symptoms",
    title: "Review of Symptoms",
    description: "Listed below are the signs and symptoms that are typical of Non-bullous impetigo. Tick all that apply:",
    fields: [
      {
        id: "typical_features",
        label: "Typical features of Non-bullous impetigo",
        value: "",
        type: "checkbox",
        options: [
          "Presents around the nose and mouth, or on the limbs or flexures",
          "Began with small reddened skin (erythematous macules)",
          "Vesicles or blisters appeared (typically less than 0.5 cm in diameter)",
          "Blisters burst leaving thick, honey-coloured or golden-brown crusts (typically around 2 cm in diameter)",
          "Lesions are not painful",
          "Some itching in the affected area",
          "Three or fewer lesions/clusters (localised presentation)",
          "Highly contagious — patient and/or carer has been counselled",
        ],
      },
      {
        id: "symptoms_typical",
        label: "Are symptoms typical of Non-bullous impetigo?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        valueNotes: {
          "No": {
            message: "Presentation is not typical of Non-bullous impetigo — refer and document reason below.",
            type: "danger",
          },
        },
      },
      {
        id: "ros_referral_reason",
        label: "Document reason for referral:",
        value: "",
        type: "textarea",
        placeholder: "Document reason for referral",
        showIf: { fieldId: "symptoms_typical", value: "No" },
      },
    ],
  },

  {
    id: "outcome",
    title: "Consultation Outcome",
    fields: [
      {
        id: "outcome_decision",
        label: "Consultation Outcome",
        value: "",
        type: "radio",
        required: true,
        options: ["Prescription for POM supplied", "OTC product supplied", "Self-care advice only", "Referral"],
      },
      {
        id: "referred_to",
        label: "Referred to",
        value: "",
        type: "select",
        options: ["Accident & Emergency Department", "General Practitioner (GP)", "GP Out-of-Hours", "Other"],
        showIf: { fieldId: "outcome_decision", value: "Referral" },
      },
      {
        id: "outcome_referral_reason",
        label: "Reason for referral",
        value: "",
        type: "textarea",
        showIf: { fieldId: "outcome_decision", value: "Referral" },
      },
      {
        id: "gp_notification",
        label: "GP Notification Sent",
        value: "",
        type: "radio",
        options: ["Yes", "No", "Not applicable"],
      },
      {
        id: "follow_up_required",
        label: "Follow-up Required",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      {
        id: "follow_up_detail",
        label: "Follow-up details",
        value: "",
        type: "textarea",
        placeholder: "e.g. Review in 72 hours if no improvement",
        showIf: { fieldId: "follow_up_required", value: "Yes" },
      },
      {
        id: "outcome_notes",
        label: "Outcome Notes",
        value: "",
        type: "textarea",
        placeholder: "Additional notes on outcome or clinical reasoning",
      },
    ],
  },
  {
    id: "treatment",
    title: "Treatment & Advice",
    fields: [
      {
        id: "proceed_prescribing",
        label: "Proceed with pharmacist prescribing?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No — Refer", "No — Patient declined treatment"],
      },
      {
        id: "no_proceed_reason",
        label: "Reason",
        value: "",
        type: "textarea",
        placeholder: "Document reason for not proceeding",
        showIf: { fieldId: "proceed_prescribing", value: ["No — Refer", "No — Patient declined treatment"] },
      },
      {
        id: "medicine_prescribed",
        label: "Medicine Prescribed",
        value: "",
        type: "select",
        required: true,
        options: [
          "Fusidic Acid 20mg/g cream — topically every 8 hours for 5–7 days",
          "Sodium Fusidate 20mg/g ointment — topically every 8 hours for 5–7 days",
        ],
        showIf: { fieldId: "proceed_prescribing", value: "Yes" },
      },
      {
        id: "quantity_supplied",
        label: "Quantity Supplied",
        value: "",
        type: "radio",
        required: true,
        options: ["15g tube", "30g tube"],
        hint: "Supply 15g tube. Only supply 30g if the 15g tube is unavailable.",
        showIf: { fieldId: "proceed_prescribing", value: "Yes" },
        valueNotes: {
          "30g tube": {
            message: "Only supply the 30g tube if the 15g tube is unavailable.",
            type: "warning",
          },
        },
      },
      {
        id: "hygiene_advice_given",
        label: "Hygiene and self-care advice given",
        value: "",
        type: "checkbox",
        hint: "Confirm which advice points were discussed with the patient or parent/guardian.",
        options: [
          "Keep lesions clean and dry",
          "Cover with loose clothing or gauze bandage",
          "Wash hands frequently, avoid touching affected area",
          "Do not share face cloths, towels, or bed linen",
          "Wash face cloths, sheets, towels at high temperature",
          "Avoid preparing food for others",
          "Avoid gym and contact sports",
          "Avoid close contact with children, diabetics, or immunocompromised people",
          "Soak off crusts with petroleum jelly (Vaseline) before applying treatment",
        ],
      },
      {
        id: "school_exclusion",
        label: "School or childcare exclusion advised",
        value: "",
        type: "radio",
        options: ["Yes", "No", "Not applicable"],
        hint: "Stay home until lesions have crusted over, or until 24 hours after starting treatment.",
      },
      {
        id: "safety_netting_items",
        label: "Safety netting advice given",
        value: "",
        type: "checkbox",
        options: [
          "Advised to seek medical review if symptoms do not improve within 72 hours of starting treatment",
          "Advised impetigo stops being contagious 24 hours after starting treatment",
          "Advised to cover unhealed lesions after 24 hours (gauze and tape) until crusted",
          "Patient Information Leaflet (PIL) provided",
          "HSE A-Z and HSE app signposted",
        ],
      },
    ],
  },

  {
    id: "signature",
    title: "Patient Declaration & Signature",
    fields: [
      { id: "impetigo_declaration", label: "Patient Declaration", value: "", type: "text", computed: true },
      {
        id: "imp_consent_hcp_sharing",
        label: "I agree to the sharing of relevant clinical information with another healthcare professional if deemed necessary by the Pharmacist, and I understand how this information will support my ongoing care.",
        value: "",
        type: "checkbox",
        options: ["Agreed"],
      },
      {
        id: "imp_consent_dispensing_info",
        label: "I understand that I can have this prescription dispensed in this pharmacy or that I can choose to have it dispensed in another pharmacy of my choice.",
        value: "",
        type: "checkbox",
        options: ["Acknowledged"],
      },
      {
        id: "dispensing_choice",
        label: "Dispensing Choice",
        value: "",
        type: "radio",
        required: true,
        options: [
          "I have chosen to have my prescription dispensed in this pharmacy",
          "I am choosing to take my prescription to another pharmacy",
        ],
      },
      { id: "patient_signature", label: "Patient Signature", value: "", type: "signature", required: true },
      {
        id: "guardian_signature",
        label: "Parent / Guardian Signature (if patient is under 16 years)",
        value: "",
        type: "signature",
        showIf: { fieldId: "patient_is_under_16", value: "Yes" },
      },
      { id: "signature_date", label: "Date", value: new Date().toLocaleDateString("en-IE"), type: "text", computed: true },
    ],
  },

  {
    id: "pharmacist_info",
    title: "Pharmacist Information",
    fields: [
      { id: "pharmacist_name", label: "First & Last Name", value: "", type: "text", required: true, placeholder: "First and last name" },
      { id: "psi_registration", label: "PSI No.", value: "", type: "text", required: true, placeholder: "e.g. 12345" },
      { id: "pharmacy_name_address", label: "Pharmacy Address", value: "", type: "textarea", required: true, placeholder: "Full pharmacy name and address" },
      { id: "pharmacy_eircode", label: "Eircode", value: "", type: "eircode", placeholder: "e.g. D02 XY12" },
      { id: "pharmacist_signature", label: "Pharmacist Signature", value: "", type: "signature", required: true },
      { id: "pharmacist_date", label: "Date", value: new Date().toLocaleDateString("en-IE"), type: "text", computed: true },
    ],
  },
];

const ORAL_THRUSH_SECTIONS: FormSection[] = [

  {
    id: "patient",
    title: "Patient Details",
    fields: [
      { id: "patient_name", label: "Patient Name", value: "", type: "text", required: true, placeholder: "Full name" },
      { id: "patient_address", label: "Address", value: "", type: "textarea", placeholder: "Home address" },
      { id: "patient_eircode", label: "Eircode", value: "", type: "eircode", placeholder: "e.g. A65 F4E2" },
      { id: "patient_ppsn", label: "PPSN", value: "", type: "ppsn", placeholder: "e.g. 1234567WA" },
      { id: "patient_phone", label: "Contact No.", value: "", type: "text", placeholder: "Contact number" },
      { id: "scheme", label: "Scheme Number", value: "", type: "radio", options: ["GMS", "DPS", "Private", "GP Visit Card"], required: true },
      { id: "patient_dob", label: "Date of Birth", value: "", type: "date", required: true, placeholder: "DD/MM/YYYY" },
      { id: "patient_age", label: "Age (auto-calculated)", value: "", type: "number", placeholder: "Auto-calculated from DOB", computed: true, displaySuffix: "years" },
      {
        id: "ot_age_warning",
        label: "Age Eligibility",
        value: "",
        type: "text",
        computed: true,
        placeholder: "Checking age eligibility...",
        hint: "Minimum age for Oral Thrush CCS service is 1 month (corrected age for pre-term infants).",
        valueNotes: {
          "under_1_month": { message: "Patient is under 1 month of age. Referral to GP/other relevant medical practitioner required.", type: "danger" },
        },
      },
      {
        id: "ot_preterm",
        label: "Pre-term birth — use corrected age",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "If the baby was born early, use the age based on their due date. Tick Yes and enter weeks pre-term below.",
      },
      {
        id: "ot_weeks_preterm",
        label: "Weeks Pre-Term",
        value: "",
        type: "number",
        showIf: { fieldId: "ot_preterm", value: "Yes" },
        placeholder: "e.g. 6",
        hint: "Number of weeks the baby was born before 40 weeks gestation.",
      },
      { id: "patient_sex", label: "Sex", value: "", type: "radio", options: ["Male", "Female", "Other"] },
      {
        id: "ot_parent_guardian",
        label: "Name of Parent / Guardian (if patient under 16 years)",
        value: "",
        type: "text",
        showIf: { fieldId: "patient_is_under_16", value: "Yes" },
        placeholder: "Required if patient is under 16 years",
        hint: "Complete this field if the patient is under 16 years of age.",
      },
      { id: "gp_name", label: "GP Name / Practice", value: "", type: "text", placeholder: "GP name (not mandatory)" },
      { id: "gp_address", label: "GP Address", value: "", type: "textarea", placeholder: "Not mandatory" },
      { id: "gp_phone", label: "GP Contact Number", value: "", type: "text", placeholder: "Not mandatory" },
      { id: "patient_consented", label: "Patient / Guardian Consent Obtained", value: "", type: "radio", options: ["Yes", "No"], required: true },
    ],
  },

  {
    id: "presenting_complaint",
    title: "Presenting Complaint",
    fields: [
      {
        id: "ot_chief_complaint",
        label: "Document the symptoms of the presenting complaint as described by the patient",
        value: "",
        type: "textarea",
        required: true,
        placeholder: "Document the symptoms as described by the patient, in their own words.",
      },
    ],
  },

  {
    id: "medical_history",
    title: "Medical History",
    fields: [
      { id: "ot_existing_conditions", label: "Existing Health Conditions", value: "", type: "textarea", placeholder: "List any relevant existing conditions" },
      { id: "ot_existing_medications", label: "Existing Medication", value: "", type: "textarea", placeholder: "List all regular medications including OTC and supplements" },
      { id: "ot_allergy_status", label: "Allergy Status", value: "", type: "textarea", placeholder: "Document any known allergies or hypersensitivities" },
      {
        id: "ot_medication_tried",
        label: "Any medication already tried for the management of oral thrush symptoms?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      {
        id: "ot_medication_tried_list",
        label: "If yes, please list:",
        value: "",
        type: "textarea",
        showIf: { fieldId: "ot_medication_tried", value: "Yes" },
        placeholder: "e.g. Nystatin oral suspension",
      },
      {
        id: "ot_pregnant_bf",
        label: "Pregnant, or suspected pregnancy / Breastfeeding",
        value: "",
        type: "checkbox",
        options: ["Pregnant, or suspected pregnancy", "Breastfeeding"],
        hint: "Pregnancy or suspected pregnancy requires GP referral. Breastfeeding is noted — Nystatin is the treatment of choice for breastfeeding mothers.",
      },
      {
        id: "ot_antimicrobial_resistance",
        label: "Is patient aware if they have resistance to previous antimicrobial treatment?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      {
        id: "ot_antimicrobial_resistance_detail",
        label: "If yes, please list:",
        value: "",
        type: "text",
        showIf: { fieldId: "ot_antimicrobial_resistance", value: "Yes" },
        placeholder: "List antimicrobial resistance details",
      },
      { id: "ot_renal_impairment", label: "Any known renal (kidney) impairment?", value: "", type: "radio", required: true, options: ["Yes", "No", "Unknown"] },
      { id: "ot_hepatic_impairment", label: "Any known hepatic (liver) impairment?", value: "", type: "radio", required: true, options: ["Yes", "No", "Unknown"] },
    ],
  },

  {
    id: "exclusions_emergency",
    title: "Red Flag and Referral Criteria",
    description: "4.1 Emergency Referral — Hospital Emergency Department or Emergency Services. If ANY of the following are present, immediate referral is required.",
    fields: [
      {
        id: "exc_ot_em_systemic",
        label: "Individual is systemically very unwell, or showing symptoms of severe/life-threatening infection, or systemic sepsis",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer urgently to Emergency Department via ambulance.", type: "danger" } },
      },
    ],
  },

  {
    id: "exclusions_urgent_gp",
    title: "Red Flag and Referral Criteria",
    description: "4.2 Urgent Medical Assessment Required. If ANY of the following are present, urgent medical assessment is required.",
    fields: [
      {
        id: "exc_ot_urg_serious",
        label: "Signs and symptoms of a more serious condition or illness",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } },
      },
      {
        id: "exc_ot_urg_complications",
        label: "Complications suspected — unable to swallow, unable to drink due to severe pain or discomfort (risk of dehydration) or a failure to respond to treatment",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } },
      },
      {
        id: "exc_ot_urg_immunocompromise",
        label: "Individual has moderate to severe immunocompromise due to underlying medical conditions or treatments",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } },
      },
    ],
  },

  {
    id: "exclusions_gp",
    title: "Red Flag and Referral Criteria",
    description: "4.3 Referral to GP, Dental Practitioner or Relevant Medical Practitioner — Pharmacist Prescribing Not Permitted. If ANY of the following are present, referral is required and pharmacist prescribing is not permitted.",
    fields: [
      { id: "exc_ot_gp_under1month", label: "Individuals under 1 month of age (corrected age for infants who are pre-term)", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_ot_gp_contraindication", label: "Contraindications as specified in the medication Summary of Product Characteristics", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_ot_gp_pregnancy", label: "Pregnancy or suspected pregnancy — refer to GP/treating obstetrician/gynaecologist", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_ot_gp_over14days", label: "Lesions are present for more than 14 days", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_ot_gp_no_resolution", label: "Signs and symptoms not resolved after 14 days of treatment", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_ot_gp_no_rub_off", label: "Presence of white spots or plaques which do not rub off", value: "", type: "radio", options: ["Yes", "No"], hint: "Oral thrush plaques rub off to reveal a red base. If they do not come off, possible leukoplakia or lichen planus.", valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_ot_gp_trauma", label: "Recent trauma to the mouth — e.g. burn or open wound", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_ot_gp_recurrence", label: "Recurrence of oral thrush", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_ot_gp_dysphagia", label: "Pain on swallowing not attributable to oral thrush", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_ot_gp_hypersensitivity", label: "Known hypersensitivity or adverse reaction to medication treatment options, or any of the components within the formulation", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_ot_gp_drug_reaction", label: "Individual has started a new medication and is presenting with symptoms of a sore mouth such as blisters or ulcerative lesions", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
    ],
  },

  {
    id: "exclusions_gp_limited_supply",
    title: "Red Flag and Referral Criteria",
    description: "4.4 Referral Required — Pharmacist May Give Initial Limited Supply. Pharmacists can consider prescribing an initial limited supply if clinically appropriate, limited to the dose or time necessary for the individual to access the referral pathway.",
    fields: [
      {
        id: "exc_ot_ls_denture",
        label: "Individuals who wear dentures should be signposted to a Dental Practitioner to review if the denture is not fitting correctly",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP/Dental Practitioner. Initial limited supply may be prescribed to bridge access to referral.", type: "warning" } },
      },
      {
        id: "exc_ot_ls_diabetes",
        label: "Individuals with a diagnosis of diabetes should be advised to see GP for a check-up to review control of blood sugar levels",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP/Dental Practitioner. Initial limited supply may be prescribed to bridge access to referral.", type: "warning" } },
      },
      {
        id: "exc_ot_ls_recurrent",
        label: "Individual who has no obvious underlying risk factors with recurrent oral thrush should be advised to see GP or Dental Practitioner for a check-up, as may be symptomatic of further underlying conditions",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP/Dental Practitioner. Initial limited supply may be prescribed to bridge access to referral.", type: "warning" } },
      },
      {
        id: "exc_ot_ls_immunocompromise",
        label: "Individual is immunocompromised due to underlying medical conditions or treatments (excluding those with moderate to severe immunocompromise — see Section 4.2)",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP/Dental Practitioner. Initial limited supply may be prescribed to bridge access to referral.", type: "warning" } },
      },
      {
        id: "exc_ot_ls_oesophageal",
        label: "Suspected oesophageal candidiasis — symptoms may include pain or discomfort when swallowing, general discomfort behind the neck and the sternum (retrosternal pain), the feeling that food is sticking in the oesophagus. This can occur with or without the presence of oropharyngeal thrush. Individuals engaged in care for a HIV infection or other immunosuppressing condition (e.g. current cancer treatment) should be referred to their treating physician.",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP/Dental Practitioner. Initial limited supply may be prescribed to bridge access to referral.", type: "warning" } },
      },
      {
        id: "ot_referral_criteria_present",
        label: "Are there any Red Flag or Referral Criteria present?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "Auto-filled based on the criteria above — you may override and add a reason below.",
        computed: true,
      },
      {
        id: "ot_referral_reason",
        label: "Reason for referral:",
        value: "",
        type: "textarea",
        showIf: { fieldId: "ot_referral_criteria_present", value: "Yes" },
        placeholder: "Document reason for referral",
      },
    ],
  },

  {
    id: "review_of_symptoms",
    title: "Review of Symptoms",
    description: "Listed below are the signs and symptoms that are typical of oral thrush. Tick all that apply:",
    fields: [
      {
        id: "ot_ros_adult",
        label: "Main Signs & Symptoms",
        value: "",
        type: "checkbox",
        options: [
          "White spots or patches adhering to the oral mucosal membrane. These white spots or patches can usually be wiped off revealing a red erythematous base (red spot), which may bleed lightly once the coating is removed.",
          "In denture wearers the infection may present as a red or sore area under their dentures",
          "May present as a red and sore mouth without white spots (especially after an individual has taken antibiotics or steroids)",
        ],
        hint: "Tick all that apply.",
      },
      {
        id: "ot_ros_infant",
        label: "Signs & Symptoms in Babies may include:",
        value: "",
        type: "checkbox",
        options: [
          "A white coating on the baby's tongue (it can look like milk, but a milk coating will come off easily)",
          "A white coating on the baby's palate (palatal white plaque which rubs off easily)",
          "White spots in the baby's mouth",
          "The baby not wanting to feed",
        ],
        hint: "Tick all that apply.",
      },
      {
        id: "ot_ros_typical",
        label: "Are symptoms typical of oral thrush?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        valueNotes: {
          "No": { message: "Refer and document.", type: "danger" },
        },
      },
      {
        id: "ot_ros_atypical_reason",
        label: "Reason for referral:",
        value: "",
        type: "textarea",
        showIf: { fieldId: "ot_ros_typical", value: "No" },
        placeholder: "Document reason for referral",
      },
    ],
  },

  {
    id: "outcome",
    title: "Consultation Outcome",
    fields: [
      {
        id: "ot_outcome_decision",
        label: "Consultation Outcome",
        value: "",
        type: "radio",
        required: true,
        options: ["Referral", "Self-care", "OTC Product Supplied", "Prescription for POM Supplied"],
      },
      {
        id: "ot_referred_to",
        label: "Referred to",
        value: "",
        type: "checkbox",
        showIf: { fieldId: "ot_outcome_decision", value: "Referral" },
        options: ["Accident & Emergency Department", "General Practitioner (GP)", "Other (please specify)"],
      },
      {
        id: "ot_referred_to_other",
        label: "Other — please specify",
        value: "",
        type: "text",
        showIf: { fieldId: "ot_outcome_decision", value: "Referral" },
        placeholder: "Specify referral destination",
      },
      {
        id: "ot_patient_declined",
        label: "Patient Declined Treatment",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      {
        id: "ot_declined_reason",
        label: "Reason for Declining",
        value: "",
        type: "textarea",
        showIf: { fieldId: "ot_patient_declined", value: "Yes" },
        placeholder: "Please give reason",
      },
    ],
  },
  {
    id: "treatment",
    title: "Treatment & Advice",
    fields: [
      {
        id: "ot_treatment_drug",
        label: "Treatment Selected",
        value: "",
        type: "radio",
        required: true,
        options: ["Nystatin 100,000 units/mL oral suspension", "Miconazole 20 mg/g oral gel"],
        hint: "Nystatin is preferred for breastfeeding mothers and infants under 4 months. Miconazole has significant drug interactions — check SPC before prescribing.",
      },

      {
        id: "ot_nystatin_dose",
        label: "Nystatin — Dose",
        value: "",
        type: "select",
        showIf: { fieldId: "ot_treatment_drug", value: "Nystatin 100,000 units/mL oral suspension" },
        options: [
          "Infant 1 month to 2 years: 1 mL dropped into mouth after feeds",
          "Infant 1 month to 2 years: 2 mL dropped into mouth after feeds",
          "Adults and children over 2 years: 1 mL after meals",
          "Adults and children over 2 years: up to 6 mL after meals",
        ],
        hint: "Keep suspension in contact with oral mucosa for as long as possible before swallowing or spitting out.",
      },
      {
        id: "ot_nystatin_frequency",
        label: "Nystatin — Frequency",
        value: "Every 6 hours (four times daily)",
        type: "text",
        showIf: { fieldId: "ot_treatment_drug", value: "Nystatin 100,000 units/mL oral suspension" },
      },
      {
        id: "ot_nystatin_duration",
        label: "Nystatin — Duration",
        value: "7 days; continue for 48 hours after clinical cure",
        type: "text",
        showIf: { fieldId: "ot_treatment_drug", value: "Nystatin 100,000 units/mL oral suspension" },
      },
      {
        id: "ot_nystatin_quantity",
        label: "Nystatin — Quantity Supplied (mL)",
        value: "",
        type: "number",
        showIf: { fieldId: "ot_treatment_drug", value: "Nystatin 100,000 units/mL oral suspension" },
      },
      {
        id: "ot_nystatin_brand",
        label: "Nystatin — Brand (optional)",
        value: "",
        type: "text",
        showIf: { fieldId: "ot_treatment_drug", value: "Nystatin 100,000 units/mL oral suspension" },
        placeholder: "Brand name if applicable",
      },

      {
        id: "ot_miconazole_dose",
        label: "Miconazole — Dose",
        value: "",
        type: "select",
        showIf: { fieldId: "ot_treatment_drug", value: "Miconazole 20 mg/g oral gel" },
        options: [
          "4 months to 2 years: 1.25 mL (quarter spoon) after feeds, four times daily",
          "Adults and children 2 years and over: 2.5 mL (half spoon) after meals, four times daily",
        ],
        hint: "Miconazole is not suitable for infants under 4 months (or under 5–6 months if pre-term or showing slow neuromuscular development). Use Nystatin.",
        valueNotes: {
          "4 months to 2 years: 1.25 mL (quarter spoon) after feeds, four times daily": { message: "Confirm patient is 4 months or older before prescribing Miconazole. For pre-term infants or those with slow neuromuscular development, use Nystatin instead.", type: "warning" },
        },
      },
      {
        id: "ot_miconazole_frequency",
        label: "Miconazole — Frequency",
        value: "Four times daily after meals",
        type: "text",
        showIf: { fieldId: "ot_treatment_drug", value: "Miconazole 20 mg/g oral gel" },
      },
      {
        id: "ot_miconazole_duration",
        label: "Miconazole — Duration",
        value: "Continue for at least 7 days after symptoms have disappeared",
        type: "text",
        showIf: { fieldId: "ot_treatment_drug", value: "Miconazole 20 mg/g oral gel" },
      },
      {
        id: "ot_miconazole_quantity",
        label: "Miconazole — Quantity Supplied (g)",
        value: "",
        type: "number",
        showIf: { fieldId: "ot_treatment_drug", value: "Miconazole 20 mg/g oral gel" },
      },
      {
        id: "ot_miconazole_brand",
        label: "Miconazole — Brand (optional)",
        value: "",
        type: "text",
        showIf: { fieldId: "ot_treatment_drug", value: "Miconazole 20 mg/g oral gel" },
        placeholder: "Brand name if applicable",
      },
      {
        id: "ot_miconazole_interactions",
        label: "Drug Interactions Checked",
        value: "",
        type: "radio",
        showIf: { fieldId: "ot_treatment_drug", value: "Miconazole 20 mg/g oral gel" },
        options: ["Yes — no significant interactions identified", "Yes — interaction identified (document below)", "Not checked"],
        hint: "Miconazole has significant drug interactions. Avoid with warfarin. Check SPC before prescribing.",
      },
      {
        id: "ot_miconazole_interaction_detail",
        label: "Interaction Detail",
        value: "",
        type: "text",
        showIf: { fieldId: "ot_miconazole_interactions", value: "Yes — interaction identified (document below)" },
        placeholder: "Document interaction and action taken",
      },

      {
        id: "ot_cotreat_header",
        label: "Contemporaneous Treatment for Breastfeeding Mother",
        value: "",
        type: "text",
        computed: true,
        showIf: { fieldId: "ot_pregnant_bf", value: "Breastfeeding" },
        placeholder: "Per protocol: if a breastfed infant has oral thrush, the mother must be treated at the same time, even if asymptomatic.",
        hint: "This block applies when the patient is a breastfed infant. The pharmacist can override if the mother is being managed separately.",
      },
      {
        id: "ot_cotreat_dose",
        label: "Miconazole 2% Cream — Apply to Nipple and Areola After Every Feed",
        value: "Apply to nipple and areola after every feed. Gently wipe off any visible cream before the next feed.",
        type: "text",
        showIf: { fieldId: "ot_pregnant_bf", value: "Breastfeeding" },
      },
      {
        id: "ot_cotreat_duration",
        label: "Co-Treatment Duration",
        value: "Continue for at least 7 days after symptoms have cleared",
        type: "text",
        showIf: { fieldId: "ot_pregnant_bf", value: "Breastfeeding" },
      },
      {
        id: "ot_cotreat_quantity",
        label: "Co-Treatment Quantity Supplied (g)",
        value: "",
        type: "number",
        showIf: { fieldId: "ot_pregnant_bf", value: "Breastfeeding" },
      },
      {
        id: "ot_cotreat_brand",
        label: "Co-Treatment Brand (optional)",
        value: "",
        type: "text",
        showIf: { fieldId: "ot_pregnant_bf", value: "Breastfeeding" },
        placeholder: "Brand name if applicable",
      },

      {
        id: "ot_counselling",
        label: "Counselling Points",
        value: "",
        type: "bullets",
        options: [
          "Brush teeth twice daily (or clean gums and tongue for edentulous patients)",
          "Discard old toothbrush during treatment; use new toothbrush after treatment",
          "Avoid overuse of antiseptic mouthwashes; warm saline water can relieve symptoms",
          "Rinse and spit after using steroid inhalers; offer inhaler technique review if applicable",
          "Rinse mouth after eating or taking medicine",
          "OTC analgesia (paracetamol or ibuprofen) if needed for pain",
          "Do not wear dentures overnight",
          "Clean and disinfect dentures (Milton 1:10 for non-metal; chlorhexidine undiluted for metal parts, max 10 minutes twice daily)",
          "Infants: sterilise soothers, bottles, teats, teething rings; wash hands before feeds and after nappy changes",
          "Breastfeeding mothers: continue breastfeeding; change breast pads after every feed; discard expressed milk stored during thrush period",
          "Smoking cessation signposting (Quit.ie) if applicable",
          "Safety netting: see GP or Dental Practitioner if symptoms persist or worsen after 14 days",
        ],
      },
    ],
  },

  {
    id: "declaration",
    title: "Patient Declaration",
    fields: [
      {
        id: "ot_declaration_bullets",
        label: "Patient Declaration",
        value: "",
        type: "bullets",
        options: [
          "I understand the nature of the condition, how the treatment works, the benefits and risks of the treatment. I understand the possible side effects of the prescription medicine issued.",
          "I have been given the opportunity to speak to the pharmacist providing the consultation and to ask questions and raise any concerns.",
          "I have been given information with regards steps to take if my condition gets worse or persists for longer than 14 days, with or without treatment.",
          "The information and details I have provided are accurate, and I understand that this will be recorded and kept by the pharmacy and shared with the HSE for the purposes of public health as required by legislation.",
          "I understand that any data collected will be processed in accordance with relevant data protection requirements.",
        ],
      },
      {
        id: "ot_consent_sharing",
        label: "I agree to the sharing of relevant clinical information with another healthcare professional if deemed necessary by the Pharmacist, and I understand how this information will support my ongoing care.",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      {
        id: "ot_consent_dispensing_info",
        label: "I understand that I can have this prescription dispensed in this pharmacy or that I can choose to have it dispensed in another pharmacy of my choice.",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      {
        id: "ot_dispensing_choice",
        label: "Dispensing Location",
        value: "",
        type: "radio",
        options: [
          "I am choosing to take my prescription to another pharmacy",
          "I have chosen to have my prescription dispensed in this pharmacy",
        ],
      },
      {
        id: "patient_is_under_16",
        label: "Patient Under 16?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        required: true,
        hint: "Auto-calculated from Date of Birth when available.",
      },
      {
        id: "patient_signature",
        label: "Signature of Person Providing Consent",
        value: "",
        type: "signature",
        required: true,
      },
      {
        id: "guardian_signature",
        label: "Signature of Parent/Guardian (if child is aged under 16 years)",
        value: "",
        type: "signature",
      },
      {
        id: "declaration_date",
        label: "Date",
        value: "",
        type: "date",
        placeholder: "DD/MM/YYYY",
      },
    ],
  },

];

const SHINGLES_PATIENT_SECTION: FormSection = {
  id: "patient",
  title: "Patient Details",
  fields: [
    { id: "patient_name", label: "Patient Name", value: "", type: "text", required: true, placeholder: "Full name" },
    { id: "patient_address", label: "Address", value: "", type: "textarea", placeholder: "Home address" },
    { id: "patient_eircode", label: "EirCode", value: "", type: "eircode", placeholder: "e.g. A65 F4E2" },
    { id: "patient_ppsn", label: "PPSN", value: "", type: "ppsn", placeholder: "e.g. 1234567WA" },
    { id: "patient_phone", label: "Contact Number", value: "", type: "text", placeholder: "Contact number" },
    { id: "scheme", label: "Scheme", value: "", type: "radio", options: ["GMS", "DPS", "GP Visit Card", "Private"], required: true },
    { id: "patient_dob", label: "Date of Birth", value: "", type: "date", required: true, placeholder: "DD/MM/YYYY" },
    { id: "patient_age", label: "Age (auto-calculated)", value: "", type: "number", placeholder: "Auto-calculated from DOB", computed: true, displaySuffix: "years" },
    {
      id: "shingles_age_warning",
      label: "Age Eligibility",
      value: "",
      type: "text",
      computed: true,
      placeholder: "Checking age eligibility...",
      valueNotes: {
        "under_18": { message: "Age under 18 — REFER to GP or other relevant medical practitioner. Pharmacist prescribing is not permitted.", type: "danger" },
      },
    },
    { id: "patient_sex", label: "Sex", value: "", type: "radio", options: ["Male", "Female", "Other"], required: true },
    { id: "patient_consented", label: "Patient Consent Obtained", value: "", type: "radio", options: ["Yes", "No"], required: true },
    { id: "gp_name", label: "GP Name / Practice", value: "", type: "text", placeholder: "GP or practice name (not mandatory)" },
    { id: "gp_address", label: "GP Address", value: "", type: "textarea", placeholder: "Not mandatory" },
    { id: "gp_phone", label: "GP Contact Number", value: "", type: "text", placeholder: "Not mandatory" },
  ],
};

const SHINGLES_SECTIONS: FormSection[] = [
  SHINGLES_PATIENT_SECTION,

  {
    id: "symptoms",
    title: "Presenting Complaint",
    fields: [
      {
        id: "chief_complaint",
        label: "Presenting Complaint",
        value: "",
        type: "textarea",
        required: true,
        placeholder: "Document the symptoms of the presenting complaint as described by the patient, in their own words.",
        hint: "Document the symptoms of the presenting complaint as described by the patient, in their own words.",
      },
    ],
  },

  {
    id: "medical_history",
    title: "Medical History",
    fields: [
      { id: "relevant_conditions", label: "Existing Health Conditions", value: "", type: "textarea", placeholder: "List any relevant existing conditions" },
      { id: "current_medications", label: "Existing Medication", value: "", type: "textarea", required: true, hint: "List all current medications including OTC and supplements." },
      { id: "known_allergies", label: "Allergy Status", value: "", type: "textarea", required: true, hint: "Include any known hypersensitivity to valaciclovir, aciclovir, famciclovir, or formulation components." },
      {
        id: "chickenpox_vaccine_history",
        label: "Has the patient ever had chickenpox, or the chickenpox or shingles vaccines?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: {
          "Yes": { message: "Refer to GP or other relevant medical practitioner.", type: "warning" },
        },
      },
      { id: "medication_tried", label: "Medication Already Tried for this Episode", value: "", type: "radio", required: true, options: ["Yes", "No"] },
      { id: "medication_tried_list", label: "Medication(s) tried", value: "", type: "textarea", placeholder: "List medication(s) tried", showIf: { fieldId: "medication_tried", value: "Yes" } },
      {
        id: "pregnant_bf",
        label: "Pregnant / Breastfeeding",
        value: "",
        type: "checkbox",
        hint: "Pregnancy or breastfeeding requires GP referral — pharmacist prescribing is not permitted.",
        options: ["Pregnant, or suspected pregnancy", "Breastfeeding"],
      },
      { id: "antimicrobial_resistance", label: "Known Resistance to Previous Antimicrobial Treatment", value: "", type: "radio", required: true, options: ["Yes", "No", "Unknown"] },
      { id: "antimicrobial_resistance_detail", label: "Please list resistance details", value: "", type: "text", showIf: { fieldId: "antimicrobial_resistance", value: "Yes" } },
      {
        id: "renal_impairment",
        label: "Known Renal (Kidney) Impairment",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No", "Unknown"],
        hint: "All three antiviral options require dose adjustment in renal impairment — see Section 3.1 of Protocol.",
      },
      { id: "hepatic_impairment", label: "Known Hepatic (Liver) Impairment", value: "", type: "radio", required: true, options: ["Yes", "No", "Unknown"] },
    ],
  },

  {
    id: "exclusions_emergency",
    title: "Exclusion Criteria",
    description: "4.1 Emergency Referral — If ANY of the following are present, refer urgently to Emergency Department via ambulance. Do NOT prescribe.",
    fields: [
      { id: "exc_em_systemic_unwell", label: "Systemically very unwell, severe / life-threatening infection, or systemic sepsis", value: "", type: "radio", options: ["Yes", "No"], yesNote: "EMERGENCY — Refer urgently to Emergency Department via ambulance. Do NOT prescribe." },
      { id: "exc_em_meningitis", label: "Suspected meningitis", value: "", type: "radio", options: ["Yes", "No"], hint: "Mottled skin, neck stiffness, photophobia.", yesNote: "EMERGENCY — Refer urgently to Emergency Department via ambulance. Do NOT prescribe." },
      { id: "exc_em_encephalitis", label: "Neurological symptoms suggesting encephalitis", value: "", type: "radio", options: ["Yes", "No"], hint: "Disorientation, confusion, behavioural changes.", yesNote: "EMERGENCY — Refer urgently to Emergency Department via ambulance. Do NOT prescribe." },
      { id: "exc_em_muscle_weakness", label: "Muscle weakness, loss of bladder or bowel control", value: "", type: "radio", options: ["Yes", "No"], hint: "Risk of myelitis.", yesNote: "EMERGENCY — Refer urgently to Emergency Department via ambulance. Do NOT prescribe." },
      { id: "exc_em_facial_palsy", label: "Facial nerve paralysis", value: "", type: "radio", options: ["Yes", "No"], hint: "May indicate stroke or Ramsay Hunt syndrome.", yesNote: "EMERGENCY — Refer urgently to Emergency Department via ambulance. Do NOT prescribe." },
      { id: "exc_em_double_vision", label: "Double vision, ptosis (lid droop), or enlarged pupil", value: "", type: "radio", options: ["Yes", "No"], hint: "Signs of cranial nerve palsy — possible CNS involvement, needs hospital physician input.", yesNote: "EMERGENCY — Refer urgently to Emergency Department via ambulance. Do NOT prescribe." },
    ],
  },

  {
    id: "exclusions_urgent_gp",
    title: "Exclusion Criteria",
    description: "4.2 Urgent Medical Assessment Required — Initial Limited Supply May Be Given.\n\nIf ANY of the following are present, urgent medical assessment is required. Pharmacists can consider prescribing an initial limited supply if clinically appropriate to mitigate the risk of delay in access to treatment.",
    fields: [
      { id: "exc_urg_hzo", label: "Suspected shingles in the ophthalmic distribution of the trigeminal nerve (Herpes Zoster Ophthalmicus)", value: "", type: "radio", options: ["Yes", "No"], hint: "Hutchinson's sign (lesions on side or tip of nose), visual symptoms, or unexplained red eye. Ophthalmology assessment within 24–48 hours recommended.", yesNote: "Urgent medical assessment required — Pathway D prescribing permitted. Initial limited supply of antiviral may be prescribed to bridge access to treatment." },
      { id: "exc_urg_ramsay_hunt", label: "Individual has suspected Ramsay Hunt Syndrome", value: "", type: "radio", options: ["Yes", "No"], hint: "Herpes zoster oticus — auricular rash with or without facial weakness.", yesNote: "Urgent medical assessment required — Pathway D prescribing permitted. Initial limited supply of antiviral may be prescribed to bridge access to treatment." },
      { id: "exc_urg_moderate_severe_immunocompromise", label: "Individual has moderate to severe immunocompromise", value: "", type: "radio", options: ["Yes", "No"], hint: "If uncertain about extent, refer to treating physician.", yesNote: "Urgent medical assessment required — Pathway D prescribing permitted. Initial limited supply of antiviral may be prescribed to bridge access to treatment." },
      { id: "exc_urg_widespread_rash", label: "Individual has a rash that is widespread or severe, or individual is systemically unwell", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Urgent medical assessment required — Pathway D prescribing permitted. Initial limited supply of antiviral may be prescribed to bridge access to treatment." },
      { id: "exc_urg_high_risk_severe", label: "Individual has a high risk of severe shingles due to severe atopic dermatitis / eczema, or presents with skin lesions that have signs of secondary infection", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Urgent medical assessment required — Pathway D prescribing permitted. Initial limited supply of antiviral may be prescribed to bridge access to treatment." },
      { id: "exc_urg_disseminated", label: "Disseminated zoster", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Urgent medical assessment required — Pathway D prescribing permitted. Initial limited supply of antiviral may be prescribed to bridge access to treatment." },
    ],
  },

  {
    id: "exclusions_gp",
    title: "Exclusion Criteria",
    description: "4.3 GP Referral — Pharmacist Prescribing Not Permitted. If ANY of the following are present, pharmacist prescribing is not permitted unless Pathway D (initial limited supply) applies.",
    fields: [
      { id: "exc_gp_under18", label: "Individual under 18 years of age", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Age under 18 — REFER to GP or other relevant medical practitioner. Pharmacist prescribing is not permitted." },
      { id: "exc_gp_contraindication", label: "Contraindications as specified in the medication Summary of Product Characteristics (SPC)", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Contraindication present — REFER to GP. Pharmacist prescribing is not permitted." },
      { id: "exc_gp_pregnancy", label: "Pregnancy or suspected pregnancy", value: "", type: "radio", options: ["Yes", "No"], yesNote: "REFER to GP, treating obstetrician, or gynaecologist. Pharmacist prescribing is not permitted." },
      { id: "exc_gp_over7days", label: "Over 7 days since vesicle formation", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Over 7 days since vesicle formation — REFER to GP. Pharmacist prescribing is not permitted." },
      { id: "exc_gp_breastfeeding", label: "Breastfeeding", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Breastfeeding — REFER to GP. Pharmacist prescribing is not permitted." },
      { id: "exc_gp_atypical", label: "Diagnosis is unclear, including atypical cutaneous presentations", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Diagnosis unclear — REFER to GP. Pharmacist prescribing is not permitted." },
      { id: "exc_gp_failed_antiviral", label: "Failure to respond to treatment with antiviral for this episode, or deterioration in symptoms", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Failure to respond to antiviral treatment — REFER to GP." },
      { id: "exc_gp_hypersensitivity", label: "Known hypersensitivity or adverse reaction to medication treatment options, or any of the components within the formulation", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Known hypersensitivity — REFER to GP. Pharmacist prescribing is not permitted." },
      { id: "exc_gp_dehydration", label: "Individual at risk of dehydration and unable to maintain adequate fluid intake", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Risk of dehydration — REFER to GP." },
      { id: "exc_gp_unable_swallow", label: "Individual unable to swallow or absorb oral medications", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Unable to swallow or absorb oral medication — REFER to GP." },
      { id: "exc_gp_antiviral_interaction", label: "Individual taking medication which interacts with anti-viral treatment", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Known interaction with antiviral treatment — REFER to GP." },
      { id: "exc_gp_outside_scope", label: "Antiviral treatment is appropriate but falls outside the scope of this protocol", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Outside scope of protocol — REFER to GP." },
      { id: "exc_gp_drug_induced_rash", label: "Individual recently started new medication and suspected drug-induced rash", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Suspected drug-induced rash — REFER to GP." },
      { id: "exc_gp_dress", label: "Individual has had a previous DRESS reaction to Valaciclovir", value: "", type: "radio", options: ["Yes", "No"], yesNote: "Previous DRESS reaction to Valaciclovir — treatment must not be restarted. REFER to GP." },
      { id: "exc_gp_summary", label: "Are there any Red Flag or Referral Criteria present?", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "exc_gp_referral_reason", label: "Reason for referral", value: "", type: "textarea", showIf: { fieldId: "exc_gp_summary", value: "Yes" } },
    ],
  },

  {
    id: "exclusions_gp_limited_supply",
    title: "Exclusion Criteria",
    description: "4.4 GP Referral Required — Initial Limited Supply Permitted (Pathway D). If ANY of the following are present, GP referral is required. Pharmacists may prescribe an initial limited supply to bridge access to the referral pathway.",
    fields: [
      { id: "exc_ls_recurrent", label: "Recurrent shingles — recurrence within 6 months of previous episode", value: "", type: "radio", options: ["Yes", "No"], yesNote: "GP referral required — Pathway D prescribing permitted. Initial limited supply may be prescribed to bridge access to treatment." },
      { id: "exc_ls_midline", label: "Rash involving more than two separate dermatomes and / or crossing midline", value: "", type: "radio", options: ["Yes", "No"], yesNote: "GP referral required — Pathway D prescribing permitted. Initial limited supply may be prescribed to bridge access to treatment." },
      { id: "exc_ls_mild_immunocompromise", label: "Mild to moderate immunocompromise", value: "", type: "radio", options: ["Yes", "No"], yesNote: "GP referral required — Pathway D prescribing permitted. Initial limited supply may be prescribed to bridge access to treatment." },
      { id: "exc_ls_vaccinated", label: "Previously vaccinated against herpes zoster", value: "", type: "radio", options: ["Yes", "No"], yesNote: "GP referral required — Pathway D prescribing permitted. Initial limited supply may be prescribed to bridge access to treatment." },
      { id: "exc_ls_long_term_antiviral", label: "Long-term oral antiviral use — may need switch to therapeutic dose", value: "", type: "radio", options: ["Yes", "No"], yesNote: "GP referral required — Pathway D prescribing permitted. Initial limited supply may be prescribed to bridge access to treatment." },
      { id: "exc_ls_genital", label: "Suspected shingles rash in genital area", value: "", type: "radio", options: ["Yes", "No"], yesNote: "GP referral required — Pathway D prescribing permitted. Initial limited supply may be prescribed to bridge access to treatment." },
      { id: "exc_ls_other_complications", label: "Other complications suspected", value: "", type: "radio", options: ["Yes", "No"], hint: "e.g. pneumonia or scarring.", yesNote: "GP referral required — Pathway D prescribing permitted. Initial limited supply may be prescribed to bridge access to treatment." },
      { id: "exc_ls_pain_control", label: "Pain inadequately controlled with OTC analgesia, or post-herpetic neuralgia suspected", value: "", type: "radio", options: ["Yes", "No"], yesNote: "GP referral required — Pathway D prescribing permitted. Initial limited supply may be prescribed to bridge access to treatment." },
    ],
  },

  {
    id: "review_of_symptoms",
    title: "Review of Symptoms",
    description: "Listed below are the signs and symptoms that are typical of shingles. Tick all that apply:",
    fields: [
      {
        id: "ros_typical_features",
        label: "Typical Features Present",
        value: "",
        type: "checkbox",
        options: [
          "Abnormal skin sensation and dermatomal pain",
          "Nerve pain — stabbing, prickling, burning or throbbing sensation",
          "Patient may feel unwell, with fever and headache",
          "Unilateral vesicular rash, typically appearing in a cutaneous distribution of one or two adjoining dermatomes, and stops sharply at the midline; in immunocompromised individuals a rash involving multiple dermatomes may occur",
          "Rash presenting as a crop of red papules, which then develop into fluid filled vesicles",
          "Vesicles scab and crust over within seven to ten days",
          "New vesicles may appear within the locality of the affected nerve for up to one week",
        ],
      },
      {
        id: "symptoms_typical",
        label: "Are the symptoms typical of shingles?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        valueNotes: { "No": { message: "Presentation is not typical of shingles — REFER and document reason.", type: "danger" } },
      },
      { id: "ros_referral_reason", label: "Reason for referral", value: "", type: "textarea", showIf: { fieldId: "symptoms_typical", value: "No" } },
    ],
  },

  {
    id: "inclusion_criteria",
    title: "Inclusion Criteria",
    description: "Confirm the patient meets at least one inclusion pathway before prescribing.",
    fields: [
      {
        id: "inclusion_pathway",
        label: "Which inclusion pathway applies?",
        value: "",
        type: "radio",
        required: true,
        options: [
          "Pathway A — Age over 50, presenting within 72 hours of rash onset",
          "Pathway B — Age 18–49, within 72 hours, with at least one of:\n  • Moderate or severe pain\n  • Moderate or severe rash\n  • Rash on arms, legs, or neck",
          "Pathway C — Age 18 or over, within 1 week of onset, with at least one of:\n  • Age 70 or over\n  • Active vesicle formation\n  • Severe pain",
          "Pathway D — Initial limited supply: age 18 or over, meets criteria in the Urgent Medical Assessment or Limited Supply exclusion tabs",
          "None of the above apply",
        ],
        valueNotes: {
          "None of the above apply": { message: "Patient does not meet inclusion criteria — REFER and document reason below.", type: "danger" },
        },
      },
      { id: "inclusion_no_criteria_reason", label: "Reason for referral (no inclusion criteria met)", value: "", type: "textarea", showIf: { fieldId: "inclusion_pathway", value: "None of the above apply" } },
    ],
  },

  {
    id: "outcome",
    title: "Consultation Outcome",
    fields: [
      {
        id: "outcome_decision",
        label: "Consultation Outcome",
        value: "",
        type: "radio",
        required: true,
        options: ["Prescription for POM supplied (full course)", "Prescription for POM supplied (initial limited supply)", "OTC product supplied", "Self-care advice only", "Referral"],
      },
      {
        id: "referred_to",
        label: "Referred To",
        value: "",
        type: "select",
        options: ["Accident & Emergency Department", "General Practitioner (GP)", "GP Out-of-Hours", "Ophthalmology (regional eye unit ED)", "Other"],
        showIf: { fieldId: "outcome_decision", value: ["Referral", "Prescription for POM supplied (initial limited supply)"] },
      },
      { id: "outcome_referral_reason", label: "Reason for Referral", value: "", type: "textarea", showIf: { fieldId: "outcome_decision", value: ["Referral", "Prescription for POM supplied (initial limited supply)"] } },
      { id: "gp_notification", label: "GP Notification Sent", value: "", type: "radio", options: ["Yes", "No", "Not applicable"] },
      {
        id: "follow_up_required",
        label: "Follow-Up Required",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
      },
      { id: "follow_up_detail", label: "Follow-Up Details", value: "", type: "textarea", showIf: { fieldId: "follow_up_required", value: "Yes" } },
      { id: "outcome_notes", label: "Additional Notes", value: "", type: "textarea" },
    ],
  },
  {
    id: "treatment",
    title: "Treatment & Advice",
    fields: [
      {
        id: "proceed_prescribing",
        label: "Proceed with Pharmacist Prescribing?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes — full course", "Yes — initial limited supply (bridge to referral)", "No — refer", "No — patient declined treatment"],
      },
      { id: "no_proceed_reason", label: "Reason", value: "", type: "textarea", showIf: { fieldId: "proceed_prescribing", value: ["No — refer", "No — patient declined treatment"] } },
      { id: "supply_days", label: "Days of Supply Provided", value: "", type: "text", hint: "Limit to dose or time necessary for the patient to access referral pathway.", showIf: { fieldId: "proceed_prescribing", value: "Yes — initial limited supply (bridge to referral)" } },
      {
        id: "medicine_prescribed",
        label: "Medicine Prescribed",
        value: "",
        type: "radio",
        required: true,
        hint: "Please see Protocol and SPCs for dosage and notes for each individual medicinal product.\n*NOTE: Be aware of dose reduction in renal impairment for all options, as per Section 3.1 of Protocol.",
        options: [
          "Valaciclovir 1g orally, every 8 hours for 7 days (1st line)",
          "Aciclovir 800mg orally, 5 times daily for 7 days (2nd line)",
          "Famciclovir 500mg orally, every 8 hours for 7 days (3rd line)",
        ],
        showIf: { fieldId: "proceed_prescribing", value: ["Yes — full course", "Yes — initial limited supply (bridge to referral)"] },
        valueNotes: {
          "Aciclovir 800mg orally, 5 times daily for 7 days (2nd line)": { message: "Second-line selected — confirm reason first-line (Valaciclovir) is not suitable.", type: "warning" },
          "Famciclovir 500mg orally, every 8 hours for 7 days (3rd line)": { message: "Third-line selected — confirm reason first-line and second-line are not suitable.", type: "warning" },
        },
      },
      {
        id: "renal_dose_adjustment",
        label: "Renal Dose Adjustment Required?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No", "Unsure — check Appendix B"],
        hint: "All three antivirals require dose adjustment in renal impairment. Refer to Protocol Appendix B (Renal Impairment Prescribing Table).",
        showIf: { fieldId: "renal_impairment", value: "Yes" },
        valueNotes: { "Yes": { message: "Renal dose adjustment confirmed — prescribe at adjusted dose per Protocol Appendix B.", type: "warning" } },
      },
      {
        id: "extended_treatment",
        label: "Extended Treatment (immunocompromised — continue for 2 days after crusting)",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "Not applicable"],
        showIf: { fieldId: "exc_urg_moderate_severe_immunocompromise", value: "Yes" },
      },
      {
        id: "selfcare_advice",
        label: "Self-Care Advice Given",
        value: "",
        type: "bullets",
        options: [
          "Patient is contagious until all vesicles have scabbed over (usually 7–10 days)",
          "Lesions may continue to appear for up to 1 week within the affected nerve area",
          "Cover exposed lesions (especially on neck) when in public",
          "Wash hands frequently with soap and water; avoid touching affected area",
          "Maintain adequate hydration",
          "Take simple OTC analgesia (paracetamol or ibuprofen) as needed for pain",
          "Rash may take up to 4 weeks to heal fully",
          "Wear loose-fitting clothing to reduce irritation",
          "Keep rash clean and dry to reduce bacterial infection risk",
          "Do NOT use topical creams or adhesive dressings",
          "Do NOT use antibiotic cream (can slow healing)",
          "Cool compress (frozen veg in a towel) may ease discomfort",
          "Seek medical advice if fever develops",
        ],
      },
      {
        id: "contact_avoidance",
        label: "Contact Avoidance — Advise Patient to Avoid Until Vesicles Have Scabbed Over",
        value: "",
        type: "bullets",
        options: [
          "People who have never had chickenpox, especially pregnant women or those with unknown immunity",
          "Immunocompromised individuals",
          "Babies under 1 month old (unless it is their own baby)",
        ],
      },
      { id: "vaccination_discussed", label: "Vaccination Discussed", value: "", type: "radio", options: ["Yes", "No", "Not applicable"], hint: "Future shingles vaccination may be considered, available privately. See NIAC Immunisation Guidelines Chapter 23." },
      {
        id: "safety_netting",
        label: "Safety Netting Advice Given",
        value: "",
        type: "bullets",
        options: [
          "Advised to see GP if symptoms persist or deteriorate beyond 7–10 days after rash onset",
          "Advised to seek review if new vesicles appearing, rash worsening, pustular discharge, or signs of superinfection",
          "Patient Information Leaflet (PIL) provided",
          "HSE A-Z and HSE app signposted",
        ],
      },
    ],
  },

  {
    id: "signature",
    title: "Patient Declaration & Signature",
    fields: [
      {
        id: "shingles_declaration",
        label: "Patient Declaration",
        value: "",
        type: "text",
        computed: true,
      },
      {
        id: "consent_hcp_sharing",
        label: "I agree to the sharing of relevant clinical information with another healthcare professional if deemed necessary by the Pharmacist in order to provide me with safe and effective care",
        value: "",
        type: "checkbox",
        options: ["Agreed"],
        required: true,
      },
      {
        id: "dispensing_choice",
        label: "Dispensing Choice",
        value: "",
        type: "radio",
        required: true,
        options: [
          "I have chosen to have my prescription dispensed in this pharmacy",
          "I am choosing to take my prescription to another pharmacy",
        ],
      },
      { id: "patient_signature", label: "Patient Signature", value: "", type: "signature", required: true },
      { id: "signature_date", label: "Date", value: new Date().toLocaleDateString("en-IE"), type: "text", computed: true },
    ],
  },
];

const VV_THRUSH_SECTIONS: FormSection[] = [

  {
    id: "patient",
    title: "Patient Details",
    fields: [
      { id: "patient_name", label: "Patient Name", value: "", type: "text", required: true, placeholder: "Full name" },
      { id: "patient_dob", label: "Date of Birth", value: "", type: "date", required: true, placeholder: "DD/MM/YYYY" },
      { id: "patient_age", label: "Age (auto-calculated)", value: "", type: "number", placeholder: "Auto-calculated from DOB", computed: true, displaySuffix: "years" },
      {
        id: "vvt_sex_gate",
        label: "Sex Eligibility",
        value: "",
        type: "text",
        computed: true,
        placeholder: "Checking sex eligibility...",
        valueNotes: {
          "male_excluded": { message: "GP REFERRAL REQUIRED — This service is for individuals whose sex assigned at birth is female. Pharmacist prescribing is not permitted (Pathway C).", type: "danger" },
        },
      },
      {
        id: "vvt_age_gate",
        label: "Age Eligibility",
        value: "",
        type: "text",
        computed: true,
        placeholder: "Checking age eligibility...",
        hint: "This service is for patients aged 16 to 60 inclusive.",
        valueNotes: {
          "age_excluded": { message: "GP REFERRAL REQUIRED — Service is for ages 16 to 60 inclusive. Pharmacist prescribing is not permitted (Pathway C).", type: "danger" },
        },
      },
      { id: "patient_sex", label: "Sex", value: "", type: "radio", options: ["Male", "Female"], required: true },
      { id: "patient_address", label: "Address", value: "", type: "textarea", placeholder: "Home address" },
      { id: "patient_eircode", label: "EirCode", value: "", type: "eircode", placeholder: "e.g. A65 F4E2" },
      { id: "patient_ppsn", label: "PPSN", value: "", type: "ppsn", placeholder: "e.g. 1234567WA" },
      { id: "patient_phone", label: "Phone", value: "", type: "text", placeholder: "Contact number" },
      { id: "scheme", label: "Scheme", value: "", type: "radio", options: ["GMS", "DPS", "GP Visit Card", "Private"], required: true },
      { id: "gp_name", label: "GP Name / Practice", value: "", type: "text", placeholder: "GP or practice name (optional)" },
      { id: "gp_address", label: "GP Address", value: "", type: "textarea", placeholder: "Optional" },
      { id: "gp_phone", label: "GP Contact Number", value: "", type: "text", placeholder: "Optional" },
      { id: "patient_consented", label: "Patient Consent Obtained", value: "", type: "radio", options: ["Yes", "No"], required: true },
    ],
  },

  {
    id: "presenting_complaint",
    title: "Presenting Complaint",
    fields: [
      {
        id: "vvt_chief_complaint",
        label: "Chief Complaint",
        value: "",
        type: "textarea",
        required: true,
        placeholder: "Document the symptoms as described by the patient, in their own words.",
      },
      {
        id: "vaginal_discharge",
        label: "Vaginal Discharge",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes – white/thick", "Yes – other", "No"],
        hint: "Frothy yellow-green or offensive odour may suggest trichomoniasis or bacterial vaginosis — consider Differential Diagnosis.",
      },
      { id: "vvt_discharge_other_desc", label: "Describe Discharge (colour, consistency, odour)", value: "", type: "text", showIf: { fieldId: "vaginal_discharge", value: "Yes – other" } },
      {
        id: "vulval_itch",
        label: "Vulval Itch / Irritation",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes – severe", "Yes – mild", "No"],
      },
      { id: "soreness", label: "Soreness / Inflammation", value: "", type: "radio", required: true, options: ["Yes", "No"] },
      { id: "dysuria", label: "External Dysuria", value: "", type: "radio", required: true, options: ["Yes", "No"] },
      { id: "duration", label: "Duration of Symptoms", value: "", type: "select", options: ["1 day", "2 days", "3 days", "4 days", "5 days", "6 days", "1 week+"] },
      {
        id: "vvt_odour",
        label: "Odour",
        value: "",
        type: "radio",
        required: true,
        options: ["None", "Non-offensive", "Offensive or 'fishy'"],
        hint: "Offensive or fishy odour suggests bacterial vaginosis rather than thrush — consider referral.",
      },
      { id: "vvt_dyspareunia", label: "Dyspareunia (Pain During Intercourse)", value: "", type: "radio", options: ["Yes", "No"] },
      {
        id: "vvt_vulval_erythema",
        label: "Vulval Erythema / Fissuring / Satellite Lesions",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        hint: "Visible redness, small cracks, or satellite spots in the skin around the vulva.",
      },
      {
        id: "vvt_abnormal_bleeding",
        label: "Abnormal Vaginal Bleeding",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        yesNote: "Abnormal bleeding outside the menstrual cycle requires GP referral.",
      },
      {
        id: "vvt_medication_tried",
        label: "Any Medication Already Tried for This Episode?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      { id: "vvt_medication_tried_list", label: "Please List Medication(s) Tried", value: "", type: "text", showIf: { fieldId: "vvt_medication_tried", value: "Yes" } },
    ],
  },

  {
    id: "medical_history",
    title: "Medical History",
    fields: [
      { id: "vvt_existing_conditions", label: "Existing Health Conditions", value: "", type: "textarea", placeholder: "List any relevant existing conditions" },
      { id: "vvt_existing_medications", label: "Existing Medications", value: "", type: "textarea", placeholder: "List all regular medications including OTC and supplements" },
      { id: "vvt_allergy_status", label: "Known Allergies", value: "", type: "textarea", placeholder: "Drug allergies, food allergies, etc.", hint: "Include any known hypersensitivity to clotrimazole, econazole, hydrocortisone, or formulation components." },
      {
        id: "vvt_immunocompromise",
        label: "Immunocompromise Due to Underlying Condition or Treatment",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        yesNote: "All immunocompromise on this form requires GP referral — Pathway C. Pharmacist prescribing is not permitted.",
      },
      {
        id: "vvt_diabetes",
        label: "Known Diabetes Mellitus (Type 1 or 2)",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        yesNote: "Diabetes requires GP referral for this condition — Pathway C.",
      },
      {
        id: "vvt_recurrent_vvc",
        label: "Recurrent Vulvovaginal Thrush (More Than 2 Episodes in Past 12 Months)",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        yesNote: "Recurrent VVC typically requires GP referral. Pharmacist discretion is permitted where there is clear exposure to known precipitants or prior clinical assessment.",
      },
      { id: "vvt_recurrent_episodes", label: "Episodes in Past 12 Months", value: "", type: "number", showIf: { fieldId: "vvt_recurrent_vvc", value: "Yes" }, placeholder: "e.g. 3" },
      {
        id: "vvt_recurrent_rationale",
        label: "Pharmacist Discretion Rationale",
        value: "",
        type: "textarea",
        showIf: { fieldId: "vvt_recurrent_vvc", value: "Yes" },
        placeholder: "Describe known precipitants or prior GP clinical assessment (leave blank to refer to GP)",
        hint: "If left blank, the auto-population will route this patient to GP referral.",
      },
      {
        id: "vvt_treatment_failure",
        label: "Failure to Respond to Previous Treatment for This Episode",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        yesNote: "Failure to respond to previous treatment requires GP referral — Pathway C.",
      },
      { id: "vvt_renal_impairment", label: "Known Renal (Kidney) Impairment", value: "", type: "radio", required: true, options: ["Yes", "No", "Unknown"] },
      { id: "vvt_hepatic_impairment", label: "Known Hepatic (Liver) Impairment", value: "", type: "radio", required: true, options: ["Yes", "No", "Unknown"] },
      {
        id: "vvt_antimicrobial_resistance",
        label: "Known Resistance to Previous Antimicrobial Treatment",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No", "Unknown"],
      },
      { id: "vvt_antimicrobial_resistance_detail", label: "Please List Resistance Details", value: "", type: "text", showIf: { fieldId: "vvt_antimicrobial_resistance", value: "Yes" } },
      {
        id: "vvt_pregnant",
        label: "Pregnant or Suspected Pregnancy",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No", "Not applicable"],
        valueNotes: {
          "Yes": { message: "Pregnancy or suspected pregnancy — REFER to GP, treating obstetrician, or gynaecologist. Pharmacist prescribing is not permitted (Pathway C).", type: "danger" },
        },
      },
      {
        id: "vvt_breastfeeding",
        label: "Currently Breastfeeding",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No", "Not applicable"],
        yesNote: "Noted on the record — pharmacist should check medication SPC and use clinical judgement.",
      },
    ],
  },

  {
    id: "exclusions_emergency",
    title: "Exclusion Criteria",
    description: "Refer urgently to Emergency Department via ambulance if present. Do NOT prescribe.",
    fields: [
      {
        id: "exc_vvt_em_systemic",
        label: "Individual is systemically very unwell, or there are symptoms of severe or life-threatening infection / systemic sepsis",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "EMERGENCY — Refer urgently to Emergency Department via ambulance. Do NOT prescribe.", type: "danger" } },
      },
    ],
  },

  {
    id: "exclusions_urgent_gp",
    title: "Exclusion Criteria",
    description: "Requires urgent medical assessment. Refer to GP / GP out-of-hours / Emergency Department. Pharmacist prescribing is not appropriate.",
    fields: [
      {
        id: "exc_vvt_urg_serious",
        label: "Signs or symptoms of a more serious condition or illness",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "Use clinical judgement — this is a general safety net for anything concerning beyond typical thrush presentation.",
        valueNotes: { "Yes": { message: "URGENT MEDICAL ASSESSMENT REQUIRED — Refer to GP / GP out-of-hours / Emergency Department.", type: "danger" } },
      },
    ],
  },

  {
    id: "exclusions_gp",
    title: "Exclusion Criteria",
    description: "GP referral required. Pharmacist prescribing is not permitted (Pathway C).",
    fields: [
      {
        id: "exc_vvt_gp_male",
        label: "Male (sex assigned at birth)",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Service is for individuals whose sex assigned at birth is female. Pharmacist prescribing not permitted (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_age",
        label: "Under 16 or Over 60 Years of Age",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Service is for ages 16 to 60 inclusive. Pharmacist prescribing not permitted (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_pregnancy",
        label: "Pregnant or Suspected Pregnancy",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Pharmacist prescribing not permitted in pregnancy (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_immunocompromise",
        label: "Immunocompromise Due to Underlying Condition or Treatment",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Immunocompromise requires GP referral (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_recurrent",
        label: "Recurrent Vulvovaginal Candidiasis (More Than 2 Episodes in Past 12 Months)",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Recurrent VVC without documented pharmacist discretion rationale (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_treatment_failure",
        label: "Failure to Respond to Previous Treatment for This Episode",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Failure to respond to treatment (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_diabetes",
        label: "Known Diabetes Mellitus (Type 1 or 2)",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Diabetes requires GP referral for this condition (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_sti",
        label: "Suspected Sexually Transmitted Infection",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        yesNote: "Signpost to GP or HSE Sexual Health services. Many STIs are asymptomatic and require testing.",
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Signpost to GP or HSE Sexual Health services (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_trichomoniasis",
        label: "Suspected Trichomoniasis",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        yesNote: "Frothy yellow-green or malodorous discharge with vulvitis. Notifiable disease — partner testing needed. Refer.",
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Suspected trichomoniasis. Notifiable disease, partner testing required (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_bv",
        label: "Suspected Bacterial Vaginosis",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        yesNote: "Thin, white, fishy-smelling discharge (often worse after intercourse). Refer.",
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Suspected bacterial vaginosis (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_menopause",
        label: "Suspected Genitourinary Syndrome of Menopause (Vulvovaginal Atrophy)",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        yesNote: "Dryness, burning, dyspareunia, or urinary symptoms in peri/post-menopausal patients.",
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Suspected genitourinary syndrome of menopause (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_lichen_sclerosus",
        label: "Suspected Lichen Sclerosus",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        yesNote: "Chronic severe itch, soreness, possible skin changes in ano-genital area. Refer.",
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Suspected lichen sclerosus (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_dermatitis",
        label: "Suspected Vulvar Dermatitis",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        yesNote: "Chronic irritation or itch, possibly linked to atopic or contact dermatitis. Refer.",
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Suspected vulvar dermatitis (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_uti",
        label: "Suspected Urinary Tract Infection",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        yesNote: "If UTI symptoms present (change in urinary frequency/urgency, abdominal pain) — consider UTI protocol instead.",
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Suspected UTI. Consider UTI protocol (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_atypical_bleeding",
        label: "Any Other Atypical Vaginal Discharge or Abnormal Vaginal Bleeding",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Atypical discharge or abnormal vaginal bleeding (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_contraindications",
        label: "Contraindications Per Medication SPC",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Contraindications to prescribed medications (Pathway C).", type: "danger" } },
      },
      {
        id: "exc_vvt_gp_hypersensitivity",
        label: "Known Hypersensitivity or Adverse Reaction to Clotrimazole, Econazole, Hydrocortisone, or Formulation Components",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "GP REFERRAL REQUIRED — Known hypersensitivity to prescribed medications (Pathway C).", type: "danger" } },
      },
    ],
  },

  {
    id: "review_of_symptoms",
    title: "Review of Symptoms",
    fields: [
      {
        id: "vvt_ros_main",
        label: "Main Signs and Symptoms",
        value: "",
        type: "checkbox",
        options: ["Vulval and/or vaginal itch", "Vaginal soreness", "Vaginal redness", "White, thick vaginal discharge"],
      },
      {
        id: "vvt_ros_other",
        label: "Other Signs and Symptoms",
        value: "",
        type: "checkbox",
        options: ["Non-offensive odour", "Dyspareunia", "Dysuria", "Vulval erythema / fissuring and/or satellite lesions"],
      },
      {
        id: "vvt_ros_typical",
        label: "Are Symptoms Typical of Vulvovaginal Thrush?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        valueNotes: {
          "No": { message: "REFER — Symptoms not typical of vulvovaginal thrush. Document reason below.", type: "danger" },
        },
      },
      { id: "vvt_ros_atypical_reason", label: "Reason Symptoms Are Not Typical (document for audit)", value: "", type: "textarea", showIf: { fieldId: "vvt_ros_typical", value: "No" } },
    ],
  },

  OUTCOME_SECTION,
  {
    id: "treatment",
    title: "Treatment & Advice",
    fields: [
      {
        id: "vvt_vulvitis_suggestion",
        label: "Decision Support — Adjunct Cream",
        value: "",
        type: "text",
        computed: true,
        placeholder: "Assessing for vulvitis indicator...",
        valueNotes: {
          "vulvitis_indicated": { message: "Vulvitis indicated — consider Clotrimazole + Hydrocortisone cream (Option F) as adjunct per protocol.", type: "info" },
        },
      },
      {
        id: "vvt_pessary_choice",
        label: "Primary Treatment — Intravaginal Pessary",
        value: "",
        type: "radio",
        required: true,
        options: [
          "Option A — Clotrimazole 500mg pessary (single dose)",
          "Option B — Clotrimazole 200mg pessary (3 consecutive nights)",
          "Option C — Clotrimazole 100mg pessary (6 consecutive nights)",
          "Option D — Econazole 150mg pessary (single dose)",
        ],
      },
      {
        id: "vvt_pessary_c_regime",
        label: "Option C — Regime",
        value: "",
        type: "radio",
        showIf: { fieldId: "vvt_pessary_choice", value: "Option C — Clotrimazole 100mg pessary (6 consecutive nights)" },
        options: ["100mg once daily for 6 nights (standard)", "2 × 100mg once daily for 3 nights (alternative)"],
      },
      {
        id: "vvt_pessary_caution_condom",
        label: "Pessary / Latex Caution",
        value: "shown",
        type: "text",
        computed: true,
        valueNotes: {
          "shown": { message: "Pessaries may damage latex condoms and diaphragms. Advise patient to use additional contraceptive precautions for 5 days after treatment.", type: "warning" },
        },
      },
      {
        id: "vvt_pessary_caution_menstrual",
        label: "Menstrual Cycle Caution",
        value: "shown",
        type: "text",
        computed: true,
        valueNotes: {
          "shown": { message: "Menstruation may displace topical treatment. Time course around menstrual cycle where possible.", type: "warning" },
        },
      },
      {
        id: "vvt_adjunct_add",
        label: "Add Topical Cream Adjunct?",
        value: "",
        type: "radio",
        options: ["Yes – add topical cream", "No"],
        hint: "Topical cream is an adjunct to the pessary — not for monotherapy.",
      },
      {
        id: "vvt_adjunct_choice",
        label: "Adjunct Cream — Choose One",
        value: "",
        type: "radio",
        showIf: { fieldId: "vvt_adjunct_add", value: "Yes – add topical cream" },
        options: [
          "Option E — Clotrimazole 1% or 2% cream",
          "Option F — Clotrimazole 1% + Hydrocortisone 1% cream",
        ],
      },
      {
        id: "vvt_counselling",
        label: "Counselling Points",
        value: "",
        type: "bullets",
        options: [
          "Wear loose clothes and cotton underwear; avoid tight clothing",
          "Avoid over-washing of genital area",
          "Avoid perfumed soaps, tea tree oil, baby wipes, vaginal deodorants, and douches",
          "Wash with emollient; pat dry with a clean towel",
          "Use non-perfumed panty liners or sanitary pads",
          "Change out of damp swimwear or sweaty sports gear promptly",
          "Thrush can be passed to sexual partners; condom use may reduce risk; partner only needs treatment if symptomatic",
          "Topical azoles reduce condom efficacy — additional contraceptive precautions advised",
          "Review if symptoms do not improve within 7 days, or return within 2 months",
          "Signpost to sexualwellbeing.ie and HSE A-Z for written information",
        ],
      },
    ],
  },
];

const CS_PATIENT_SECTION: FormSection = {
  id: "patient",
  title: "Patient Details",
  fields: [
    { id: "patient_name", label: "Patient Name", value: "", type: "text", required: true, placeholder: "Full name" },
    { id: "patient_address", label: "Address", value: "", type: "textarea", placeholder: "Home address" },
    { id: "patient_eircode", label: "EirCode", value: "", type: "eircode", placeholder: "e.g. A65 F4E2" },
    { id: "patient_ppsn", label: "PPSN", value: "", type: "ppsn", placeholder: "e.g. 1234567WA" },
    { id: "patient_phone", label: "Contact Number", value: "", type: "text", placeholder: "Contact number" },
    { id: "scheme", label: "Scheme Number", value: "", type: "radio", options: ["GMS", "DPS", "Private", "GP Visit Card"], required: true },
    { id: "patient_dob", label: "Date of Birth", value: "", type: "date", required: true, placeholder: "DD/MM/YYYY" },
    { id: "patient_age", label: "Age (auto-calculated)", value: "", type: "number", placeholder: "Auto-calculated from DOB", computed: true, displaySuffix: "years" },
    {
      id: "cs_age_warning",
      label: "Age Eligibility",
      value: "",
      type: "text",
      computed: true,
      placeholder: "Checking age eligibility...",
      hint: "Minimum age for Cold Sores CCS service is 1 month (corrected age for pre-term infants).",
      valueNotes: {
        "under_1_month": { message: "Patient is under 1 month of age. Referral to GP/other relevant medical practitioner required.", type: "danger" },
      },
    },
    { id: "patient_sex", label: "Sex", value: "", type: "radio", options: ["Male", "Female", "Other"], required: true },
    {
      id: "guardian_name",
      label: "Name of Parent / Guardian",
      value: "",
      type: "text",
      placeholder: "Required if patient is under 16 years",
      hint: "Complete this field if the patient is under 16 years of age.",
      showIf: { fieldId: "patient_is_under_16", value: "Yes" },
    },
    { id: "patient_consented", label: "Patient Consent Obtained", value: "", type: "radio", options: ["Yes", "No"], required: true },
    { id: "gp_name", label: "GP Name / Practice **", value: "", type: "text", placeholder: "Not mandatory" },
    { id: "gp_address", label: "GP Address **", value: "", type: "textarea", placeholder: "Not mandatory" },
    { id: "gp_phone", label: "GP Contact Number **", value: "", type: "text", placeholder: "Not mandatory" },
  ],
};

const COLD_SORES_SECTIONS: FormSection[] = [
  CS_PATIENT_SECTION,

  {
    id: "symptoms",
    title: "Presenting Complaint",
    fields: [
      {
        id: "cs_chief_complaint",
        label: "Document the symptoms of the presenting complaint as described by the patient",
        value: "",
        type: "textarea",
        required: true,
        placeholder: "Document the symptoms as described by the patient, in their own words.",
        hint: "Document the symptoms as described by the patient, in their own words.",
      },
    ],
  },

  {
    id: "medical_history",
    title: "Medical History",
    fields: [
      { id: "existing_conditions", label: "Existing Health Conditions", value: "", type: "textarea", placeholder: "List any relevant existing conditions" },
      { id: "existing_medication", label: "Existing Medication", value: "", type: "textarea", placeholder: "List all current medications including OTC" },
      { id: "known_allergies", label: "Allergy Status", value: "", type: "textarea", placeholder: "List any known allergies or intolerances" },
      { id: "cs_med_tried", label: "Any medication already tried for the management of cold sores symptoms?", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "cs_med_tried_list", label: "If yes, please list:", value: "", type: "textarea", showIf: { fieldId: "cs_med_tried", value: "Yes" } },
      {
        id: "pregnant_bf",
        label: "Pregnant, or suspected pregnancy / Breastfeeding",
        value: "",
        type: "checkbox",
        options: ["Pregnant, or suspected pregnancy", "Breastfeeding"],
      },
      {
        id: "cs_pregnancy_warning",
        label: "",
        value: "",
        type: "text",
        computed: true,
        valueNotes: {
          "pregnant": {
            message: "Pregnancy or suspected pregnancy — refer to GP, treating obstetrician, or gynaecologist. Pharmacist prescribing is not permitted (Protocol Section 2.4.3).",
            type: "danger",
          },
        },
      },
      { id: "cs_antimicrobial_resistance", label: "Is patient aware if they have resistance to previous antimicrobial treatment?", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "cs_antimicrobial_resistance_detail", label: "If yes, please list:", value: "", type: "text", showIf: { fieldId: "cs_antimicrobial_resistance", value: "Yes" } },
      { id: "renal_impairment", label: "Any known renal (kidney) impairment?", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "hepatic_impairment", label: "Any known hepatic (liver) impairment?", value: "", type: "radio", options: ["Yes", "No"] },
    ],
  },

  {
    id: "exclusions_emergency",
    title: "Exclusion Criteria",
    description: "4.1 Emergency Referral — Hospital Emergency Department or Emergency Services\n\nIf ANY of the following are present, immediate referral is required.",
    fields: [
      { id: "exc_cs_em_systemic", label: "Individual is systemically very unwell, or showing symptoms of severe/life-threatening infection, or systemic sepsis", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Refer urgently to Emergency Department via ambulance.", type: "danger" } } },
    ],
  },

  {
    id: "exclusions_urgent_gp",
    title: "Exclusion Criteria",
    description: "4.2 Urgent Medical Assessment Required\n\nIf ANY of the following are present, urgent medical assessment is required.",
    fields: [
      { id: "exc_cs_urg_under1month", label: "Individuals under 1 month of age (corrected age for infants who are pre-term)", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
      { id: "exc_cs_urg_eye", label: "Lesions involving the eye", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
      { id: "exc_cs_urg_immunocompromise", label: "Individual has moderate to severe immunocompromise due to underlying medical conditions or treatments", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
    ],
  },

  {
    id: "exclusions_gp",
    title: "Exclusion Criteria",
    description: "4.3 Referral to GP or Relevant Medical Practitioner — Pharmacist Prescribing Not Permitted\n\nIf ANY of the following are present, referral is required and pharmacist prescribing is not permitted.",
    fields: [
      { id: "exc_cs_gp_contraindication", label: "Contraindications as specified in the medication Summary of Product Characteristics", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_cs_gp_pregnancy", label: "Pregnancy or suspected pregnancy — refer to GP/treating obstetrician/gynaecologist", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_cs_gp_spreading", label: "Signs of infection spreading", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_cs_gp_over14days", label: "Symptoms not improving within 14 days, with or without treatment", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_cs_gp_secondary", label: "Signs of secondary infection — e.g. very painful or very swollen", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_cs_gp_gingivostomatitis", label: "Suspected gingivostomatitis — or refer to Dental Practitioner", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_cs_gp_erythema_multiforme", label: "Suspected erythema multiforme", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_cs_gp_hypersensitivity", label: "Known hypersensitivity or adverse reaction to medication treatment options, or any of the components within the formulation", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
    ],
  },

  {
    id: "exclusions_gp_limited_supply",
    title: "Exclusion Criteria",
    description: "4.4 Referral Required — Pharmacist May Give Initial Limited Supply\n\nPharmacists can consider prescribing an initial limited supply if clinically appropriate, limited to the dose or time necessary for the individual to access the referral pathway.",
    fields: [
      {
        id: "exc_cs_ls_immunocompromise",
        label: "Individual is immunocompromised due to underlying medical conditions or treatments (excluding those with moderate to severe immunocompromise — see Section 4.2)",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Initial limited supply may be prescribed to bridge access to referral.", type: "warning" } },
      },
      {
        id: "exc_cs_ls_recurrent",
        label: "Recurrent problematic lesions or frequently recurrent infection — to ensure no underlying immunocompromise or another diagnosis",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        valueNotes: { "Yes": { message: "Refer to GP. Initial limited supply may be prescribed to bridge access to referral.", type: "warning" } },
      },
      { id: "cs_red_flag_present", label: "Are there any Red Flag or Referral Criteria present?", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "cs_referral_reason_exc", label: "Reason for referral:", value: "", type: "textarea", showIf: { fieldId: "cs_red_flag_present", value: "Yes" } },
    ],
  },

  {
    id: "review_of_symptoms",
    title: "Review of Symptoms",
    description: "Listed below are the signs and symptoms that are typical of cold sores. Tick all that apply:",
    fields: [
      {
        id: "cs_typical_features",
        label: "Typical Features",
        value: "",
        type: "checkbox",
        options: [
          "Prodromal phase — tingling, itching or burning feeling on lip",
          "Fluid-filled blisters may appear, weep and crust over into a scab",
          "Swollen and tender glands",
          "Clusters of blisters or sores may develop inside the mouth — known as Gingivostomatitis (If present, refer as per Section 2.4.3 of the HSE Protocol)",
        ],
      },
      {
        id: "cs_review_child_symptoms",
        label: "Other symptoms in children may include:",
        value: "",
        type: "checkbox",
        options: [
          "Sore gums",
          "Sore throat and swollen glands",
          "More saliva than normal",
          "High temperature",
          "Headaches",
          "Refusal to drink fluids",
        ],
      },
      {
        id: "cs_typical_confirmed",
        label: "Are symptoms typical of cold sores?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        valueNotes: {
          "No": { message: "Refer and document.", type: "danger" },
        },
      },
      { id: "cs_atypical_reason", label: "Reason for referral:", value: "", type: "textarea", showIf: { fieldId: "cs_typical_confirmed", value: "No" } },
    ],
  },

  {
    id: "outcome",
    title: "Consultation Outcome",
    fields: [
      {
        id: "outcome_decision",
        label: "Consultation Outcome",
        value: "",
        type: "radio",
        required: true,
        options: ["Referral", "Self-care", "OTC Product Supplied", "Prescription for POM Supplied", "Patient Declined Treatment"],
      },
      {
        id: "cs_referred_to",
        label: "Referred To",
        value: "",
        type: "checkbox",
        options: ["Accident & Emergency Department", "General Practitioner (GP)", "Other (please specify)"],
        showIf: { fieldId: "outcome_decision", value: "Referral" },
      },
      { id: "cs_referral_other_detail", label: "Other referral destination (please specify):", value: "", type: "text", showIf: { fieldId: "outcome_decision", value: "Referral" } },
      { id: "cs_notes_referral", label: "Notes", value: "", type: "textarea", placeholder: "Additional notes on referral", showIf: { fieldId: "outcome_decision", value: "Referral" } },
      { id: "cs_notes_selfcare", label: "Notes", value: "", type: "textarea", placeholder: "Additional notes on self-care advice given", showIf: { fieldId: "outcome_decision", value: "Self-care" } },
      { id: "cs_notes_otc", label: "Notes", value: "", type: "textarea", placeholder: "Additional notes on OTC product supplied", showIf: { fieldId: "outcome_decision", value: "OTC Product Supplied" } },
      { id: "cs_notes_pom", label: "Notes", value: "", type: "textarea", placeholder: "Additional notes on prescription supplied", showIf: { fieldId: "outcome_decision", value: "Prescription for POM Supplied" } },
      { id: "cs_notes_declined", label: "Notes", value: "", type: "textarea", placeholder: "Reason patient declined treatment", showIf: { fieldId: "outcome_decision", value: "Patient Declined Treatment" } },
    ],
  },
  {
    id: "treatment",
    title: "Treatment & Advice",
    fields: [
      {
        id: "cs_antiviral_needed",
        label: "Is Antiviral Treatment Necessary?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes — proceed with prescribing", "No — self-limiting recurrence, self-care advice only", "No — patient declined treatment", "No — refer"],
        hint: "The protocol notes that many individuals experience occasional self-limiting recurrences with minimal symptoms where antiviral treatment may not be necessary. Non-pharmacological options (topical analgesics, mouthwash, lip barrier preparations) can also be considered.",
      },
      {
        id: "cs_proceed_prescribing",
        label: "Proceed with Pharmacist Prescribing?",
        value: "",
        type: "radio",
        required: true,
        showIf: { fieldId: "cs_antiviral_needed", value: "Yes — proceed with prescribing" },
        options: ["Yes — full course", "Yes — initial limited supply (bridge to referral)"],
      },
      {
        id: "cs_days_supply",
        label: "Days of Supply Provided",
        value: "",
        type: "text",
        showIf: { fieldId: "cs_proceed_prescribing", value: "Yes — initial limited supply (bridge to referral)" },
        placeholder: "Number of days",
        hint: "Limit to dose or time necessary for the patient to access the referral pathway.",
      },
      {
        id: "cs_medicine",
        label: "Medicine Prescribed",
        value: "",
        type: "radio",
        required: true,
        showIf: { fieldId: "cs_antiviral_needed", value: "Yes — proceed with prescribing" },
        options: ["Aciclovir 5% w/w cream — apply 5 times daily at approximately 4-hourly intervals (omitting the night-time application), for at least 4 days. If healing has not occurred, treatment may be continued for up to 10 days."],
        hint: "Apply as soon as possible, preferably during prodromal or erythema stage. Treatment can still be started during papule or blister stages.",
      },
      {
        id: "cs_nonpharm_options",
        label: "Non-Pharmacological Options Discussed",
        value: "",
        type: "checkbox",
        options: ["Topical analgesic (for pain)", "Mouthwash (for oral discomfort)", "Lip barrier preparation (to prevent cracking)", "SPF 30+ lip balm (for sun-triggered recurrences)"],
        hint: "Consider suitability of these alongside or instead of antiviral treatment.",
      },
      {
        id: "cs_self_care",
        label: "Self-Care Advice Given",
        value: "",
        type: "checkbox",
        required: true,
        options: ["Drink lots of fluids (water or milk)", "Eat cool, soft foods", "Avoid salty or citrus foods (can make blisters sting)", "Wash hands often with soap and warm water after touching the cold sore or applying cream", "Wash child's clothes separately (if child has cold sore)", "Avoid trigger factors where possible (e.g. SPF for sun, stress management)"],
        hint: "Confirm which advice points were discussed with the patient or parent/guardian.",
      },
      {
        id: "cs_infection_control",
        label: "Infection Control Advice — Things NOT to Do",
        value: "",
        type: "checkbox",
        required: true,
        options: ["Touch cold sores (except to apply cream)", "Share anything that contacts cold sores (creams, cutlery, towels)", "Kiss anyone while cold sore is active", "Engage in oral sex until completely healed", "Allow anyone with a cold sore to kiss a baby or child (risk of neonatal herpes, can be fatal)"],
        hint: "Advised patient NOT to do the following, to reduce risk of spreading to others or autoinoculation.",
      },
      {
        id: "cs_childcare_advice",
        label: "Childcare / School Advice",
        value: "",
        type: "checkbox",
        showIf: { fieldId: "patient_is_under_16", value: "Yes" },
        options: ["Child can attend childcare/school if well enough", "Support child to follow hygiene practices while cold sore is weeping", "Avoid contact sports or skin-to-skin play until completely healed and symptoms resolved", "Clean gym equipment and mats regularly"],
        hint: "Advise the parent/guardian on the following.",
      },
      {
        id: "cs_safety_netting",
        label: "Safety Netting Advice Given",
        value: "",
        type: "checkbox",
        required: true,
        options: ["Advised to see GP if symptoms deteriorate", "Advised to see GP if symptoms persist longer than 14 days, with or without treatment", "Patient Information Leaflet (PIL) provided", "HSE A-Z and HSE app signposted"],
      },
    ],
  },

  {
    id: "signature",
    title: "Patient Declaration & Signature",
    fields: [
      { id: "cs_declaration", label: "Patient Declaration", value: "", type: "text", computed: true },
      {
        id: "cs_consent_hcp_sharing",
        label: "I agree to the sharing of relevant clinical information with another healthcare professional if deemed necessary by the Pharmacist, and I understand how this information will support my ongoing care.",
        value: "",
        type: "checkbox",
        options: ["Agreed"],
      },
      {
        id: "cs_consent_dispensing_info",
        label: "I understand that I can have this prescription dispensed in this pharmacy or that I can choose to have it dispensed in another pharmacy of my choice.",
        value: "",
        type: "checkbox",
        options: ["Acknowledged"],
      },
      {
        id: "cs_dispensing_choice",
        label: "Dispensing Choice",
        value: "",
        type: "radio",
        required: true,
        options: [
          "I have chosen to have my prescription dispensed in this pharmacy",
          "I am choosing to take my prescription to another pharmacy",
        ],
      },
      { id: "patient_signature", label: "Signature of person providing consent", value: "", type: "signature", required: true },
      {
        id: "guardian_signature",
        label: "Signature of parent/guardian providing consent (if patient is under 16 years)",
        value: "",
        type: "signature",
        showIf: { fieldId: "patient_is_under_16", value: "Yes" },
      },
      { id: "signature_date", label: "Date", value: new Date().toLocaleDateString("en-IE"), type: "text", computed: true },
    ],
  },

  {
    id: "pharmacist_info",
    title: "Pharmacist Information",
    fields: [
      { id: "pharmacist_name", label: "First & Last Name", value: "", type: "text", required: true },
      { id: "psi_registration", label: "PSI No.", value: "", type: "text", required: true },
      { id: "pharmacy_name_address", label: "Pharmacy Address", value: "", type: "textarea", required: true },
      { id: "pharmacy_eircode", label: "Eircode", value: "", type: "eircode" },
      { id: "pharmacist_signature", label: "Pharmacist Signature", value: "", type: "signature", required: true },
      { id: "pharmacist_date", label: "Date", value: new Date().toLocaleDateString("en-IE"), type: "text", computed: true },
    ],
  },
];

const UTI_SECTIONS: FormSection[] = [

  {
    id: "patient",
    title: "Patient Details",
    fields: [
      { id: "patient_name", label: "Patient Name", value: "", type: "text", required: true, placeholder: "Full name" },
      { id: "patient_address", label: "Address", value: "", type: "textarea", placeholder: "Home address" },
      { id: "patient_eircode", label: "EirCode", value: "", type: "eircode", placeholder: "e.g. A65 F4E2" },
      { id: "patient_ppsn", label: "PPSN", value: "", type: "ppsn", placeholder: "e.g. 1234567WA" },
      { id: "patient_phone", label: "Contact Number", value: "", type: "text", placeholder: "Contact number" },
      { id: "scheme", label: "Scheme Number", value: "", type: "radio", options: ["GMS", "DPS", "Private", "GP Visit Card"], required: true },
      { id: "patient_dob", label: "Date of Birth", value: "", type: "date", required: true, placeholder: "DD/MM/YYYY" },
      { id: "patient_age", label: "Age (auto-calculated)", value: "", type: "number", placeholder: "Auto-calculated from DOB", computed: true, displaySuffix: "years" },
      { id: "patient_sex", label: "Sex", value: "", type: "radio", options: ["Male", "Female", "Other"], required: true },
      {
        id: "uti_sex_gate",
        label: "Sex Eligibility",
        value: "",
        type: "text",
        computed: true,
        placeholder: "Checking sex eligibility...",
        valueNotes: {
          "excluded": { message: "Referral to GP/other relevant medical practitioner required.", type: "danger" },
        },
      },
      {
        id: "uti_age_gate",
        label: "Age Eligibility",
        value: "",
        type: "text",
        computed: true,
        placeholder: "Checking age eligibility...",
        valueNotes: {
          "age_excluded": { message: "Referral to GP/other relevant medical practitioner required.", type: "danger" },
        },
      },
      { id: "patient_consented", label: "Patient Consent Obtained", value: "", type: "radio", options: ["Yes", "No"], required: true },
      { id: "gp_name", label: "GP Name *", value: "", type: "text", placeholder: "GP or practice name", hint: "* Not mandatory" },
      { id: "gp_address", label: "GP Address *", value: "", type: "textarea", placeholder: "GP address", hint: "* Not mandatory" },
      { id: "gp_phone", label: "GP Contact Number *", value: "", type: "text", placeholder: "GP contact number", hint: "* Not mandatory" },
    ],
  },

  {
    id: "presenting_complaint",
    title: "Presenting Complaint",
    fields: [
      {
        id: "uti_chief_complaint",
        label: "Document the symptoms of the presenting complaint as described by the patient",
        value: "",
        type: "textarea",
        required: true,
        placeholder: "Document the symptoms as described by the patient.",
      },
    ],
  },

  {
    id: "medical_history",
    title: "Medical History",
    fields: [
      { id: "uti_existing_conditions", label: "Existing Health Conditions", value: "", type: "textarea", placeholder: "Existing conditions" },
      { id: "uti_existing_medications", label: "Existing Medication", value: "", type: "textarea", placeholder: "Current medications" },
      { id: "uti_allergy_status", label: "Allergy Status", value: "", type: "textarea", placeholder: "Known allergies" },
      { id: "uti_meds_tried", label: "Any medication already tried for the management of UTI symptoms?", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "uti_meds_tried_list", label: "If yes, please list:", value: "", type: "textarea", showIf: { fieldId: "uti_meds_tried", value: "Yes" }, placeholder: "List medications tried" },
      {
        id: "uti_pregnant_bf",
        label: "Pregnancy or suspected pregnancy / Breastfeeding",
        value: "",
        type: "checkbox",
        hint: "Pregnancy or suspected pregnancy requires urgent medical assessment",
        options: ["Pregnancy or suspected pregnancy", "Breastfeeding"],
      },
      {
        id: "uti_preg_consequence",
        label: "",
        value: "",
        type: "text",
        computed: true,
        valueNotes: {
          "pregnant": { message: "Refer for urgent medical assessment.", type: "danger" },
        },
      },
      {
        id: "uti_prophylactic_antibiotics",
        label: "If already taking prophylactic antibiotics for UTI",
        value: "",
        type: "checkbox",
        hint: "Taking prophylactic antibiotics for UTI requires GP referral",
        options: ["Currently taking prophylactic antibiotics for UTI"],
      },
      {
        id: "uti_prophylactic_consequence",
        label: "",
        value: "",
        type: "text",
        computed: true,
        valueNotes: {
          "active": { message: "Refer to GP/other relevant medical practitioner.", type: "danger" },
        },
      },
      {
        id: "uti_antimicrobial_resistance",
        label: "Is patient aware if they have resistance to previous antimicrobial treatment?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
      },
      { id: "uti_antimicrobial_resistance_detail", label: "If yes, please list:", value: "", type: "textarea", showIf: { fieldId: "uti_antimicrobial_resistance", value: "Yes" }, placeholder: "List resistance details" },
      { id: "uti_renal_impairment", label: "Any known renal (kidney) impairment?", value: "", type: "radio", options: ["Yes", "No"] },
      { id: "uti_hepatic_impairment", label: "Any known hepatic (liver) impairment?", value: "", type: "radio", options: ["Yes", "No"] },
    ],
  },

  {
    id: "exclusions_emergency",
    title: "Red Flag and Referral Criteria",
    description: "4.1 Emergency Referral — Hospital Emergency Department or Emergency Services. If ANY of the following are present, immediate referral is required.",
    fields: [
      { id: "exc_uti_em_systemic", label: "Individual is systemically very unwell, or showing symptoms of severe/life-threatening infection, or systemic sepsis", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Refer urgently to Emergency Department via ambulance.", type: "danger" } } },
      { id: "exc_uti_em_confusion", label: "Confusion, drowsiness, or slurred speech", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Refer urgently to Emergency Department via ambulance.", type: "danger" } } },
      { id: "exc_uti_em_no_urine", label: "Unable to pass urine all day", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Refer urgently to Emergency Department via ambulance.", type: "danger" } } },
      { id: "exc_uti_em_severe_worsening", label: "Severe symptoms getting worse quickly", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Refer urgently to Emergency Department via ambulance.", type: "danger" } } },
      { id: "exc_uti_em_breathing", label: "Difficulty breathing", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Refer urgently to Emergency Department via ambulance.", type: "danger" } } },
      { id: "exc_uti_em_skin_changes", label: "Skin changes — pain, redness, swelling, rash, discharge, mottling/blistering", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Refer urgently to Emergency Department via ambulance.", type: "danger" } } },
      { id: "exc_uti_em_blood_clots", label: "Presence of blood clots in the urine unexplained by menstruation", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Refer urgently to Emergency Department via ambulance.", type: "danger" } } },
    ],
  },

  {
    id: "exclusions_urgent_gp",
    title: "Red Flag and Referral Criteria",
    description: "4.2 Urgent Medical Assessment Required. If ANY of the following are present, urgent medical assessment is required.",
    fields: [
      { id: "exc_uti_urg_serious", label: "Signs and symptoms of a more serious condition or illness including raised temperature or chills within the past 48 hours", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
      { id: "exc_uti_urg_complicated", label: "Suspected complicated UTI — e.g. pyelonephritis", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
      { id: "exc_uti_urg_cauda_equina", label: "Suspected Cauda Equina Syndrome", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
      { id: "exc_uti_urg_incontinence_only", label: "Individual presenting with new onset urinary incontinence only", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
      { id: "exc_uti_urg_immunocompromise", label: "Individual is immunocompromised due to underlying medical conditions or treatments", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
      { id: "exc_uti_urg_diabetes", label: "Known diabetes mellitus (Type 1 or 2)", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
      { id: "exc_uti_urg_catheter", label: "Individual is using a urinary catheter", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
      { id: "exc_uti_urg_pregnancy", label: "Pregnancy or suspected pregnancy", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
      { id: "exc_uti_urg_ckd", label: "Known Chronic Kidney Disease (CKD) or renal insufficiency or individual is on dialysis", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Urgent medical assessment required. Refer to treating service / GP / GP out of hours / hospital emergency department.", type: "danger" } } },
    ],
  },

  {
    id: "exclusions_gp",
    title: "Red Flag and Referral Criteria",
    description: "4.3 Referral to GP or Relevant Medical Practitioner — Pharmacist Prescribing Not Permitted. If ANY of the following are present, referral is required and pharmacist prescribing is not permitted.",
    fields: [
      { id: "exc_uti_gp_male", label: "Male assigned at birth", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_age_under16", label: "Individuals under 16 years of age", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_age_over65", label: "Individuals aged 65 years of age and over", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_haematuria", label: "All individuals self-reporting visible haematuria (frank haematuria) as a symptom", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_structural", label: "Individuals with known urinary tract structural abnormalities", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_recurrent", label: "Recurrent UTI — one UTI within the previous six months prior to this presentation OR two UTIs within the previous twelve months prior to this presentation", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_resistance", label: "Known resistance to previous treatment for UTI", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_prophylactic", label: "Individuals already taking prophylactic antibiotics for UTI", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_urethritis", label: "Suspected urethritis — inflammation post sexual intercourse, irritants", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_nursing_home", label: "Nursing home or residential care facility resident", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_vaginal_discharge", label: "Abnormal vaginal discharge", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_menopause", label: "Suspected genitourinary syndrome of menopause", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_sti", label: "Suspected sexually transmitted infection", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_treatment_failure", label: "Failure to respond to treatment for this episode of UTI or there is a deterioration in symptoms", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_contraindications", label: "Contraindications as specified in the medication Summary of Product Characteristics", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_hypersensitivity", label: "Known hypersensitivity or adverse reaction to medication treatment options, or any of the components within the formulation (including individuals with rare hereditary problems of galactose intolerance, total lactase deficiency or glucose-galactose malabsorption)", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_unable_swallow", label: "Individuals unable to swallow or absorb oral medications", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      { id: "exc_uti_gp_hospitalisation", label: "Hospitalisation for greater than 7 days in last 6 months OR hospitalisation in a foreign country within last 3 months", value: "", type: "radio", options: ["Yes", "No"], valueNotes: { "Yes": { message: "Referral required. Pharmacist prescribing is not permitted.", type: "danger" } } },
      {
        id: "uti_referral_criteria_present",
        label: "Are there any Red Flag or Referral Criteria present?",
        value: "",
        type: "radio",
        options: ["Yes", "No"],
        hint: "Auto-filled based on the criteria above — you may override and add a reason below.",
        computed: true,
      },
      {
        id: "uti_referral_reason",
        label: "Reason for referral:",
        value: "",
        type: "textarea",
        showIf: { fieldId: "uti_referral_criteria_present", value: "Yes" },
        placeholder: "Document reason for referral",
      },
    ],
  },

  {
    id: "review_of_symptoms",
    title: "Review of Symptoms",
    description: "Listed below are the signs and symptoms that are typical of uncomplicated lower UTI in non-pregnant females under 65 years. Tick all that apply:",
    fields: [
      {
        id: "uti_ros_checklist",
        label: "Symptoms",
        value: "",
        type: "checkbox",
        options: [
          "Acute dysuria",
          "New/worsening urinary frequency",
          "New/worsening urinary urgency",
          "Suprapubic pain",
          "Nocturia",
          "Patient has confirmed absence of visible haematuria",
          "Patient has confirmed absence of new onset of vaginal discharge or a significant change in vaginal discharge",
          "New onset urinary incontinence (e.g. involuntary leakage of urine not previously present) in conjunction with some of the other symptoms listed",
        ],
      },
      {
        id: "uti_ros_typical",
        label: "Are symptoms typical of uncomplicated lower UTI (cystitis)?",
        value: "",
        type: "radio",
        required: true,
        options: ["Yes", "No"],
        valueNotes: {
          "No": { message: "Refer and document.", type: "danger" },
        },
      },
      { id: "uti_ros_atypical_reason", label: "Reason for referral:", value: "", type: "textarea", showIf: { fieldId: "uti_ros_typical", value: "No" }, placeholder: "Document reason for referral" },
    ],
  },

  OUTCOME_SECTION,
  {
    id: "treatment",
    title: "Treatment & Advice",
    fields: [
      {
        id: "uti_tx_pathway",
        label: "Treatment Pathway",
        value: "",
        type: "text",
        computed: true,
        placeholder: "Determined by core symptom ticks in Presenting Complaint...",
        valueNotes: {
          "antibiotic": { message: "All three core symptoms present (dysuria, frequency, urgency) — Antibiotic treatment indicated per protocol.", type: "info" },
          "wait_and_see": { message: "One or two core symptoms present — 48-Hour Wait and See approach indicated.", type: "warning" },
          "not_indicated": { message: "No core UTI symptoms recorded. Treatment not indicated under this protocol. Consider alternative diagnosis or self-care advice.", type: "warning" },
        },
      },

      {
        id: "uti_off_label_note",
        label: "Off-Label Prescribing Notice",
        value: "shown",
        type: "text",
        computed: true,
        showIf: { fieldId: "uti_tx_pathway", value: "antibiotic" },
        valueNotes: {
          "shown": { message: "3-day course is per HSE AMRIC guidance and differs from SPC. Document in consultation notes.", type: "warning" },
        },
      },

      {
        id: "uti_antibiotic_choice",
        label: "Antibiotic Treatment — Select One",
        value: "",
        type: "radio",
        required: true,
        options: [
          "Option A — Nitrofurantoin Immediate Release 50 mg capsules",
          "Option B — Nitrofurantoin Prolonged Release 100 mg capsules",
          "Option C — Trimethoprim 200 mg tablets",
        ],
      },

      {
        id: "uti_a_caution",
        label: "Option A — Caution",
        value: "shown",
        type: "text",
        computed: true,
        showIf: { fieldId: "uti_antibiotic_choice", value: "Option A — Nitrofurantoin Immediate Release 50 mg capsules" },
        valueNotes: {
          "shown": { message: "Dose: 50 mg every 6 hours. Contraindicated in eGFR < 45 mL/min/1.73m². Do not use in breastfeeding mothers if infant is under 1 month, or if infant has known/suspected G6PD deficiency or other erythrocyte enzyme deficiency. Prescription must specify Immediate Release formulation.", type: "warning" },
        },
      },

      {
        id: "uti_b_caution",
        label: "Option B — Caution",
        value: "shown",
        type: "text",
        computed: true,
        showIf: { fieldId: "uti_antibiotic_choice", value: "Option B — Nitrofurantoin Prolonged Release 100 mg capsules" },
        valueNotes: {
          "shown": { message: "Dose: 100 mg every 12 hours. Contraindicated in eGFR < 45 mL/min/1.73m². Do not use in breastfeeding mothers if infant is under 1 month, or if infant has known/suspected G6PD deficiency or other erythrocyte enzyme deficiency. Prescription must specify Prolonged Release formulation.", type: "warning" },
        },
      },

      {
        id: "uti_c_note",
        label: "Option C — Note",
        value: "shown",
        type: "text",
        computed: true,
        showIf: { fieldId: "uti_antibiotic_choice", value: "Option C — Trimethoprim 200 mg tablets" },
        valueNotes: {
          "shown": { message: "Dose: 200 mg every 12 hours. Use when nitrofurantoin is unsuitable — breastfeeding (infant < 1 month), nitrofurantoin allergy, or eGFR < 45. Safe in breastfeeding.", type: "info" },
        },
      },

      {
        id: "uti_wait_and_see",
        label: "48-Hour Wait and See Approach — Advice Checklist",
        value: "",
        type: "checkbox",
        showIf: { fieldId: "uti_tx_pathway", value: "wait_and_see" },
        options: [
          "Paracetamol and/or ibuprofen for pain relief (if no contraindications)",
          "Maintain adequate fluid intake",
          "Hot water bottle for suprapubic discomfort",
          "Return for re-assessment after 48 hours if symptoms persist",
          "Seek urgent medical attention if: becomes systemically unwell, develops visible haematuria, symptoms deteriorate, or develops fever/flank pain/nausea/vomiting (possible pyelonephritis)",
        ],
      },

      {
        id: "uti_counselling",
        label: "Counselling Points",
        value: "",
        type: "bullets",
        options: [
          "Drink plenty of water",
          "Avoid sexual intercourse during cystitis",
          "Avoid perfumed bubble bath, soaps, talc, cleansing wipes, disinfectants around genital area",
          "Use emollient-based product or plain warm water to wash",
          "Shower rather than bathe",
          "Empty bladder fully (double-void) and at first urge",
          "Wipe front to back",
          "Empty bladder after intercourse",
          "Wear breathable cotton underwear; avoid tight trousers",
          "Evidence for cranberry products and probiotics is limited — some find them helpful",
          "Safety netting: return or seek medical advice if symptoms don't improve within 48 hours, or deteriorate at any point",
        ],
      },
    ],
  },
];

const PHARMACIST_INFO_SECTION: FormSection = {
  id: "pharmacist_info",
  title: "Pharmacist Information",
  fields: [
    { id: "pharmacist_name", label: "First & Last Name", value: "", type: "text", required: true, placeholder: "First and last name" },
    { id: "psi_registration", label: "PSI No.", value: "", type: "text", required: true, placeholder: "e.g. 12345" },
    { id: "pharmacy_name_address", label: "Pharmacy Address", value: "", type: "textarea", required: true, placeholder: "Full pharmacy name and address" },
    { id: "pharmacy_eircode", label: "Eircode", value: "", type: "eircode", placeholder: "e.g. D02 XY12" },
    { id: "pharmacist_signature", label: "Pharmacist Signature", value: "", type: "signature", required: true },
    { id: "pharmacist_date", label: "Date", value: new Date().toLocaleDateString("en-IE"), type: "text", computed: true },
  ],
};

const FORM_SECTIONS: Record<CCSCondition, FormSection[]> = {
  conjunctivitis_infective: [...CONJUNCTIVITIS_SECTIONS, SIGNATURE_SECTION, PHARMACIST_INFO_SECTION],
  rhinitis_allergic: [...RHINITIS_SECTIONS, SIGNATURE_SECTION, PHARMACIST_INFO_SECTION],
  impetigo: IMPETIGO_SECTIONS,
  oral_thrush: [...ORAL_THRUSH_SECTIONS, PHARMACIST_INFO_SECTION],
  shingles: [...SHINGLES_SECTIONS, PHARMACIST_INFO_SECTION],
  vv_thrush: [...VV_THRUSH_SECTIONS, SIGNATURE_SECTION, PHARMACIST_INFO_SECTION],
  cold_sores: COLD_SORES_SECTIONS,
  uti: [...UTI_SECTIONS, SIGNATURE_SECTION, PHARMACIST_INFO_SECTION],
};

/**
 * The ordered section/field schema for a condition.
 *
 * Throws for a condition that has no declared schema. Returning `[]` here (the
 * previous behaviour) meant a corrupt or unrecognised `condition` produced a
 * *blank* consultation form: the pharmacist would see zero fields, nothing to
 * complete, and could still save a clinically empty record. For the app's
 * highest-stakes data path, an undeclared condition is a programming/data
 * error and must fail loudly at the boundary rather than degrade silently.
 *
 * Callers that legitimately handle arbitrary strings (e.g. rendering a record
 * out of Firestore) must narrow with `isCCSCondition` first — see
 * `groupFormDataIntoSections`.
 */
export function getFormSections(condition: CCSCondition): FormSection[] {
  const sections = FORM_SECTIONS[condition];
  if (!sections) {
    throw new Error(
      `Unknown CCS condition "${String(condition)}" — no form schema is declared for it. ` +
        `Declare it in FORM_SECTIONS (packages/consultation-forms/src/formSchema.ts) ` +
        `or narrow the value with isCCSCondition() before calling getFormSections().`,
    );
  }
  return sections;
}

export function buildInitialFormData(condition: CCSCondition): Record<string, string> {
  const sections = getFormSections(condition);
  const data: Record<string, string> = {};
  for (const section of sections) {
    for (const field of section.fields) {
      data[field.id] = field.value ?? "";
    }
  }
  return data;
}
